import React, { useState, useEffect, useMemo, useTransition, useDeferredValue, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import './GroupSharePage.css';
import { useAuth } from '../context/AuthContext'; // Import useAuth
import { authFetch } from '../services/authFetch'; // Import authFetch
import { getMerchants, getMerchantCommodities, savePriceDataFromGroupShare, createGroupShareLink, updateGroupShareLink } from '../services/supabaseClient'; // Import Supabase functions
import SelectLocationModal from '../components/SelectLocationModal'; // Import the new modal
import TransactionModal from '../components/cargo/TransactionModal';
import ScuIncompatibilityWarning from '../components/cargo/ScuIncompatibilityWarning';
import LocationCascadeSelector from '../components/route/LocationCascadeSelector'; // PRD-025: Import LocationCascadeSelector
import { listOrgWallets, createOrgTransaction } from '../services/orgWallet';
import { createOrgTransactionsBulk } from '../services/orgWallet';
// PRD-042 Phase 5: Price Contribution Summary
import PriceContributionSummary from '../components/trading/PriceContributionSummary';

const scuOptions = [1, 2, 4, 8, 16, 24, 32];
const defaultHaulItem = { id: Date.now(), commodityId: '', locationId: '', kioskId: '', quantity: 1, scu: 1, totalUEC: 0, avgPrice: 0, actualPrice: '' };
const defaultPlayer = { name: '', share: 100, paid: false, id: null, notify: false };
const walletCategories = ['Trade','Bounties','Salvage','Piracy','Other'];

const GroupSharePage = () => {
    const [allCommodities, setAllCommodities] = useState([]);
    const [allMerchants, setAllMerchants] = useState([]);
    const [merchantCommodities, setMerchantCommodities] = useState([]);
    const [avgByCommodityFallback, setAvgByCommodityFallback] = useState({});
    const [avgSellByCommodityFallback, setAvgSellByCommodityFallback] = useState({});
    const [minSellByCommodityFallback, setMinSellByCommodityFallback] = useState({});
    // Recent buy price history by commodity and merchant (fallback when snapshots are missing)
    const [recentBuyMerchantsMap, setRecentBuyMerchantsMap] = useState({}); // { [commodityId]: { [merchantId]: lastPrice } }
    // Recent sell price history by commodity and merchant (fallback for sell-side suggestions)
    const [recentSellMerchantsMap, setRecentSellMerchantsMap] = useState({}); // { [commodityId]: { [merchantId]: lastPrice } }
    const [haulItems, setHaulItems] = useState([defaultHaulItem]);
    const [players, setPlayers] = useState([defaultPlayer]);
    const [memo, setMemo] = useState('');
    const [miscEarnings, setMiscEarnings] = useState('');
    const { currentUser, token, logout, hasPremiumAccess, orgMemberships, supabase } = useAuth(); // Get current user, token, and logout from auth context
    const [copiedIndex, setCopiedIndex] = useState(null); // To track which button was clicked
    const { transactionId } = useParams(); // Get transactionId from URL
    const [routeMode, setRouteMode] = useState('fastSell');
    const [selectedSystem, setSelectedSystem] = useState('');
    const [selectedMajorPlanet, setSelectedMajorPlanet] = useState('');
    const [currentLocation, setCurrentLocation] = useState(null); // PRD-025: LocationCascadeSelector state
    const navigate = useNavigate(); // Add navigate
    const [isLocationModalOpen, setLocationModalOpen] = useState(false);
    const [selectedItemForInventory, setSelectedItemForInventory] = useState(null);
    const [sellModalOpen, setSellModalOpen] = useState(false);
    const [sellIndex, setSellIndex] = useState(null);
    // Auto-apply route suggestions after switching modes (fast/best)
    // Values: '' | 'missing' | 'all'
    const [pendingAutoApply, setPendingAutoApply] = useState('');
    const draftKey = 'gs_draft_v1';
    const [showDraftBanner, setShowDraftBanner] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    // Route Preview Modal State
    const [showRouteModal, setShowRouteModal] = useState(false);

    const [,startTransition] = useTransition();
    const [lineItems, setLineItems] = useState([{ category: 'Trade', amount: '', notes: '' }]);

    // Save to Org modal state
    const [showSaveModal, setShowSaveModal] = useState(false);
    const [orgOptions, setOrgOptions] = useState([]); // [{id, name}]
    const [selectedOrg, setSelectedOrg] = useState('');
    const [walletOptions, setWalletOptions] = useState([]); // [{id, name}]
    const [selectedWallet, setSelectedWallet] = useState('');
    const [confirmSave, setConfirmSave] = useState(false);
    const [isSavingOrg, setIsSavingOrg] = useState(false);
    const [orgSaveError, setOrgSaveError] = useState('');
    const [splitPerMember, setSplitPerMember] = useState(false);

    // Username autocomplete state
    const [nameSuggestIdx, setNameSuggestIdx] = useState(null);
    const [nameSuggestions, setNameSuggestions] = useState([]);
    
    // Share link state
    const [shareLink, setShareLink] = useState('');
    const [showShareModal, setShowShareModal] = useState(false);
    const [activeShareId, setActiveShareId] = useState(null);
    const [isLiveShare, setIsLiveShare] = useState(false);
    const [showShareWarning, setShowShareWarning] = useState(true);
    // PRD-042 Phase 5: Track submitted prices for contribution summary
    const [submittedPricesCount, setSubmittedPricesCount] = useState(0);
    const [submittedCommodityNames, setSubmittedCommodityNames] = useState([]);

    // Deferred values to keep inputs responsive while heavy calcs run
    const deferredHaulItems = useDeferredValue(haulItems);
    const deferredAllMerchants = useDeferredValue(allMerchants);
    const deferredAllCommodities = useDeferredValue(allCommodities);
    const deferredSystem = useDeferredValue(selectedSystem);
    const deferredMajor = useDeferredValue(selectedMajorPlanet);

    // Average market buy price per SCU across all merchants for each commodity
    const marketAvgByCommodity = useMemo(() => {
        const map = {};
        try {
            const byCommodity = new Map();
            (merchantCommodities || []).forEach(mc => {
                if (!mc || mc.activity !== 'buy') return;
                let last = null;
                if (mc.last_buy_price != null && !Number.isNaN(Number(mc.last_buy_price))) {
                    last = Number(mc.last_buy_price);
                } else if (mc.price_history && mc.price_history.length > 0) {
                    const lastIdx = mc.price_history.length - 1;
                    last = Number(mc.price_history[lastIdx].price) || 0;
                }
                if (last != null) {
                    if (!byCommodity.has(mc.commodity_id)) byCommodity.set(mc.commodity_id, []);
                    byCommodity.get(mc.commodity_id).push(last);
                }
            });
            byCommodity.forEach((arr, cid) => {
                if (arr.length > 0) {
                    const avg = arr.reduce((a, b) => a + b, 0) / arr.length;
                    map[cid] = avg;
                }
            });
        } catch (_) {}
        return map;
    }, [merchantCommodities]);

    const marketMaxBuyByCommodity = useMemo(() => {
        const map = {};
        try {
            const byCommodity = new Map();
            (merchantCommodities || []).forEach(mc => {
                if (!mc || mc.activity !== 'buy') return;
                let last = null;
                // Prefer snapshot if present
                if (mc.last_buy_price != null && !Number.isNaN(Number(mc.last_buy_price))) {
                    last = Number(mc.last_buy_price);
                } else if (mc.price_history && mc.price_history.length > 0) {
                    last = Number(mc.price_history[mc.price_history.length - 1]?.price) || 0;
                }
                if (last != null) {
                    const arr = byCommodity.get(mc.commodity_id) || [];
                    arr.push(last);
                    byCommodity.set(mc.commodity_id, arr);
                }
            });
            byCommodity.forEach((arr, cid) => {
                if (arr.length > 0) map[cid] = Math.max(...arr);
            });
        } catch (_) {}
        return map;
    }, [merchantCommodities]);

    const marketAvgSellByCommodity = useMemo(() => {
        const map = {};
        try {
            const byCommodity = new Map();
            (merchantCommodities || []).forEach(mc => {
                if (!mc || mc.activity !== 'sell') return;
                let last = null;
                if (mc.last_sell_price != null && !Number.isNaN(Number(mc.last_sell_price))) {
                    last = Number(mc.last_sell_price);
                } else if (mc.price_history && mc.price_history.length > 0) {
                    last = Number(mc.price_history[mc.price_history.length - 1]?.price) || 0;
                }
                if (last != null) {
                    if (!byCommodity.has(mc.commodity_id)) byCommodity.set(mc.commodity_id, []);
                    byCommodity.get(mc.commodity_id).push(last);
                }
            });
            byCommodity.forEach((arr, cid) => {
                if (arr.length > 0) map[cid] = (arr.reduce((a,b)=>a+b,0)/arr.length);
            });
        } catch(_) {}
        return map;
    }, [merchantCommodities]);

    // Merchant lookup to apply System/Major filters to fallbacks and scoped stats
    const merchantById = useMemo(() => {
        const map = new Map();
        try { (allMerchants || []).forEach(m => map.set(m.id, m)); } catch(_) {}
        return map;
    }, [allMerchants]);

    // Min SELL price per commodity within current System/Major selection
    const marketMinSellByCommodityScoped = useMemo(() => {
        const map = {};
        try {
            const byCommodity = new Map();
            (merchantCommodities || []).forEach(mc => {
                if (!mc || mc.activity !== 'sell') return;
                const merch = merchantById.get(mc.merchant_id);
                if (!merch) return;
                if (selectedSystem && merch.system !== selectedSystem) return;
                if (selectedMajorPlanet && getMerchantMajor(merch) !== selectedMajorPlanet) return;
                let last = null;
                if (mc.last_sell_price != null && !Number.isNaN(Number(mc.last_sell_price))) {
                    last = Number(mc.last_sell_price);
                } else if (mc.price_history && mc.price_history.length > 0) {
                    last = Number(mc.price_history[mc.price_history.length - 1]?.price) || 0;
                }
                if (last != null && Number.isFinite(last) && last > 0) {
                    const arr = byCommodity.get(mc.commodity_id) || [];
                    arr.push(last);
                    byCommodity.set(mc.commodity_id, arr);
                }
            });
            byCommodity.forEach((arr, cid) => { if (arr.length > 0) map[cid] = Math.min(...arr); });
        } catch(_) {}
        return map;
    }, [merchantCommodities, merchantById, selectedSystem, selectedMajorPlanet]);

    // Fallback: fetch recent price_history for selected commodities to compute averages/min/max when snapshots are missing
    useEffect(() => {
        (async () => {
            try {
                const commodityIds = Array.from(new Set(haulItems.map(h => h.commodityId).filter(Boolean)));
                if (commodityIds.length === 0) { setAvgByCommodityFallback({}); setAvgSellByCommodityFallback({}); setMinSellByCommodityFallback({}); return; }
                const missingAvgBuy = commodityIds.filter(cid => !marketAvgByCommodity[cid] || marketAvgByCommodity[cid] === 0);
                const missingAvgSell = commodityIds.filter(cid => !marketAvgSellByCommodity[cid] || marketAvgSellByCommodity[cid] === 0);
                const missingMaxBuy = commodityIds.filter(cid => !marketMaxBuyByCommodity[cid] || marketMaxBuyByCommodity[cid] === 0);
                // For min sell scoped fallback
                const missingMinSellScoped = commodityIds.filter(cid => !marketMinSellByCommodityScoped[cid]);
                if (missingAvgBuy.length === 0 && missingAvgSell.length === 0 && missingMaxBuy.length === 0 && missingMinSellScoped.length === 0) {
                    setAvgByCommodityFallback({}); setAvgSellByCommodityFallback({}); setMinSellByCommodityFallback({}); return;
                }
                const sinceIso = new Date(Date.now() - 30*24*60*60*1000).toISOString();
                const { data, error } = await supabase
                    .from('price_history')
                    .select('commodity_id, merchant_id, price, created_at, activity')
                    .in('commodity_id', Array.from(new Set([...missingAvgBuy, ...missingAvgSell, ...missingMaxBuy, ...missingMinSellScoped])))
                    .gte('created_at', sinceIso);
                if (error) throw error;
                const accBuyAvg = {};
                const accSellAvg = {};
                const accBuyMax = {};
                const accSellMinScoped = {};
                const accRecentBuyMerchants = {}; // { [cid]: { [merchantId]: lastPrice } }
                const accRecentSellMerchants = {}; // { [cid]: { [merchantId]: lastPrice } }
                (data || []).forEach(r => {
                    const cid = r.commodity_id;
                    const p = Number(r.price);
                    const act = String(r.activity || '').toLowerCase();
                    const merch = merchantById.get(r.merchant_id);
                    if (!Number.isFinite(p)) return;
                    if (act === 'buy') {
                        const s = accBuyAvg[cid] || { sum: 0, count: 0 };
                        s.sum += p; s.count += 1; accBuyAvg[cid] = s;
                        accBuyMax[cid] = Math.max(accBuyMax[cid] || -Infinity, p);
                        // track most recent buy price per merchant for fastSell fallback ranking
                        const m = accRecentBuyMerchants[cid] || {};
                        const prev = m[r.merchant_id];
                        // use latest timestamp
                        if (!prev || new Date(r.created_at) > new Date(prev.ts)) {
                            m[r.merchant_id] = { price: p, ts: r.created_at };
                        }
                        accRecentBuyMerchants[cid] = m;
                    } else if (act === 'sell') {
                        const s2 = accSellAvg[cid] || { sum: 0, count: 0 };
                        s2.sum += p; s2.count += 1; accSellAvg[cid] = s2;
                        if (merch) {
                            if (selectedSystem && merch.system !== selectedSystem) return;
                            if (selectedMajorPlanet && getMerchantMajor(merch) !== selectedMajorPlanet) return;
                            accSellMinScoped[cid] = Math.min(accSellMinScoped[cid] ?? Infinity, p);
                            // track most recent sell price per merchant
                            const m2 = accRecentSellMerchants[cid] || {};
                            const prev2 = m2[r.merchant_id];
                            if (!prev2 || new Date(r.created_at) > new Date(prev2.ts)) {
                                m2[r.merchant_id] = { price: p, ts: r.created_at };
                            }
                            accRecentSellMerchants[cid] = m2;
                        }
                    }
                });
                const avgBuyMap = {};
                const avgSellMap = {};
                const maxBuyMap = {};
                const minSellMap = {};
                Object.entries(accBuyAvg).forEach(([cid, s]) => { avgBuyMap[cid] = s.count > 0 ? (s.sum / s.count) : 0; });
                Object.entries(accSellAvg).forEach(([cid, s]) => { avgSellMap[cid] = s.count > 0 ? (s.sum / s.count) : 0; });
                Object.entries(accBuyMax).forEach(([cid, v]) => { if (Number.isFinite(v)) maxBuyMap[cid] = v; });
                Object.entries(accSellMinScoped).forEach(([cid, v]) => { if (Number.isFinite(v)) minSellMap[cid] = v; });
                setAvgByCommodityFallback(avgBuyMap);
                setAvgSellByCommodityFallback(avgSellMap);
                setMinSellByCommodityFallback(minSellMap);
                // flatten recent buy merchant map to simple { cid: { merchantId: price } }
                const flat = {};
                Object.entries(accRecentBuyMerchants).forEach(([cid, m]) => {
                    flat[cid] = Object.fromEntries(Object.entries(m).map(([mid, obj]) => [mid, obj.price]));
                });
                setRecentBuyMerchantsMap(flat);
                const flatSell = {};
                Object.entries(accRecentSellMerchants).forEach(([cid, m]) => {
                    flatSell[cid] = Object.fromEntries(Object.entries(m).map(([mid, obj]) => [mid, obj.price]));
                });
                setRecentSellMerchantsMap(flatSell);
            } catch(_) { setAvgByCommodityFallback({}); }
        })();
    }, [haulItems, supabase, marketAvgByCommodity, marketAvgSellByCommodity, marketMaxBuyByCommodity, marketMinSellByCommodityScoped, merchantById, selectedSystem, selectedMajorPlanet]);

    useEffect(() => {
        const fetchTransactionDetails = async () => {
            if (transactionId) {
                try {
                    const response = await fetch(`/api/ledger/${transactionId}`);
                    if (!response.ok) {
                        throw new Error('Failed to fetch transaction details.');
                    }
                    const transaction = await response.json();
                    
                    if(transaction) {
                        setHaulItems(transaction.haulItems.map(item => ({ ...defaultHaulItem, ...item })) || [defaultHaulItem]);
                        setPlayers(transaction.players.map(p => ({ ...defaultPlayer, ...p })) || [defaultPlayer]);
                        setMiscEarnings(transaction.miscEarnings || '');
                        setMemo(transaction.memo || '');
                    }
                } catch (error) {
                    console.error("Error fetching transaction details:", error);
                }
            }
        };

        fetchTransactionDetails();
    }, [transactionId]);

    // Draft restore check on mount (only when creating new)
    useEffect(() => {
        if (transactionId) return;
        try {
            const raw = localStorage.getItem(draftKey);
            if (raw) {
                const d = JSON.parse(raw);
                if (d && (Array.isArray(d.haulItems) || Array.isArray(d.players) || Array.isArray(d.lineItems) || typeof d.memo === 'string')) {
                    setShowDraftBanner(true);
                }
            }
        } catch (_) {}
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Persist Route Planning filters
    useEffect(() => {
        try {
            const s = localStorage.getItem('gs_system');
            const p = localStorage.getItem('gs_major');
            if (s) setSelectedSystem(s);
            if (p) setSelectedMajorPlanet(p);
        } catch(_) {}
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => {
        try { localStorage.setItem('gs_system', selectedSystem || ''); } catch(_) {}
    }, [selectedSystem]);
    useEffect(() => {
        try { localStorage.setItem('gs_major', selectedMajorPlanet || ''); } catch(_) {}
    }, [selectedMajorPlanet]);

    // Auto-save draft (only when creating new)
    useEffect(() => {
        if (transactionId) return;
        try {
            const payload = { haulItems, lineItems, players, memo };
            localStorage.setItem(draftKey, JSON.stringify(payload));
        } catch (_) {}
    }, [haulItems, lineItems, players, memo, transactionId]);

    useEffect(() => {
        const fetchCommodities = async () => {
            try {
                // PRD-025: Fix commodity dropdown to use items table (single source of truth)
                const { data, error } = await supabase
                    .from('items')
                    .select('id, name, category')
                    .eq('category', 'Commodities')
                    .order('name');

                if (error) throw error;
                setAllCommodities(data || []);
            } catch (error) {
                console.error("Failed to fetch commodities:", error);
            }
        };
        const fetchMerchants = async () => {
            try {
                const data = await getMerchants();
                setAllMerchants(data);
            } catch (error) {
                console.error("Failed to fetch merchants:", error);
            }
        };
        const fetchMerchantCommodities = async () => {
            try {
                const data = await getMerchantCommodities();
                setMerchantCommodities(data);
            } catch (error) {
                console.error("Failed to fetch merchant commodities:", error);
            }
        };
        fetchCommodities();
        fetchMerchants();
        fetchMerchantCommodities();
    }, []);

    const openSaveToOrg = async () => {
        // Check if user is authenticated
        if (!currentUser) {
            const proceed = window.confirm(
                "You need to be signed in to save to an organization.\n\n" +
                "Would you like to sign in now?"
            );
            if (proceed) {
                navigate('/login');
            }
            return;
        }

        // Check if user has premium access
        if (!hasPremiumAccess) {
            alert(
                "Save to Org is a premium feature.\n\n" +
                "Upgrade to premium to save transactions to your organization wallet.\n\n" +
                "Free users: Transactions are saved for 24 hours.\n" +
                "Premium users: Permanent transaction storage + org wallet integration."
            );
            return;
        }

        // Check if user belongs to an org
        if (!orgMemberships || orgMemberships.length === 0) {
            const proceed = window.confirm(
                "You are not currently a member of any organization.\n\n" +
                "To save transactions to an org wallet, you need to join or create an organization.\n\n" +
                "Would you like to go to your profile to create one?"
            );
            if (proceed) {
                navigate('/profile?tab=subscriptions');
            }
            return;
        }

        setShowSaveModal(true);
        setConfirmSave(false);
        setOrgSaveError('');
        try {
            const ids = Array.from(new Set((orgMemberships || []).map(m => m.org_id).filter(Boolean)));
            let options = ids.map(id => ({ id, name: id }));
            if (ids.length > 0) {
                try {
                    const { data } = await supabase
                        .from('orgs')
                        .select('id,name,slug')
                        .in('id', ids);
                    if (Array.isArray(data)) {
                        options = ids.map(id => ({ id, name: (data.find(d => d.id === id)?.name) || id }));
                    }
                } catch (_) {}
            }
            setOrgOptions(options);
            if (options.length === 1) {
                setSelectedOrg(options[0].id);
                const { data: ws } = await listOrgWallets(options[0].id);
                setWalletOptions(ws || []);
            }
        } catch (_) {
            setOrgOptions([]);
        }
    };

    const handleSelectOrg = async (orgId) => {
        setSelectedOrg(orgId);
        setSelectedWallet('');
        setWalletOptions([]);
        if (!orgId) return;
        const { data: ws } = await listOrgWallets(orgId);
        setWalletOptions(ws || []);
    };

    const continueToConfirm = () => {
        setOrgSaveError('');
        if (!selectedOrg || !selectedWallet) {
            setOrgSaveError('Select an org and a wallet.');
            return;
        }
        setConfirmSave(true);
    };

    const prepareShareData = () => {
        // Calculate profit data
        const acquisitionCost = haulItems.reduce((sum, item) => {
            const qty = Number(item.quantity || 0);
            const scu = Number(item.scu || 0);
            const avgPrice = Number(item.avgPrice || 0);
            return sum + (qty * scu * avgPrice);
        }, 0);
        
        const revenue = totalUECEarned || 0;
        const profit = revenue - acquisitionCost;
        const margin = acquisitionCost > 0 ? ((profit / acquisitionCost) * 100).toFixed(2) : 0;
        
        // Prepare the data for sharing
        return {
            // Acquisition data
            haulItems: haulItems.map(item => ({
                commodityId: item.commodityId,
                commodityName: allCommodities.find(c => c.id === item.commodityId)?.name || '',
                locationId: item.locationId,
                locationName: allMerchants.find(m => m.id === item.locationId)?.name || '',
                quantity: item.quantity,
                scu: item.scu,
                avgPrice: item.avgPrice,
                totalUEC: item.totalUEC,
                actualPrice: item.actualPrice,
                leftover: item.leftover
            })),
            
            // Route information
            routeMode: routeMode,
            currentLocation: {
                system: selectedSystem,
                planet: selectedMajorPlanet
            },
            
            // Player shares
            players: players.map(p => ({
                name: p.name,
                share: p.share,
                paid: p.paid
            })),
            
            // Summary data
            totalAcquisitionCost: acquisitionCost,
            totalUECEarned: revenue,
            totalProfit: profit,
            profitMargin: margin,
            memo: memo,
            
            // Metadata
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            createdBy: currentUser?.username || 'Anonymous'
        };
    };

    const generateShareLink = async () => {
        // Check if user is authenticated
        if (!currentUser) {
            const proceed = window.confirm(
                "You need to be signed in to generate a share link.\n\n" +
                "Would you like to sign in now?"
            );
            if (proceed) {
                navigate('/login');
            }
            return;
        }
        
        try {
            const shareData = prepareShareData();
            
            // Check if user has premium access
            const isPremium = hasPremiumAccess || currentUser?.tier === 'paid' || currentUser?.org_entitled;
            
            // Try to create a live share using Supabase
            const result = await createGroupShareLink(shareData, isPremium);
            
            if (result.error === 'daily_limit') {
                // Show daily limit message for free users
                setRouteToast(`⚠️ ${result.message}`);
                setTimeout(() => setRouteToast(''), 8000);
                
                // Show upgrade modal or message
                alert(result.message + '\n\nVisit /purchase to upgrade your account.');
                return;
            }
            
            if (result.success && result.shareId) {
                // Successfully created live share
                const link = `${window.location.origin}/groupshare/view/${result.shareId}`;
                setShareLink(link);
                setActiveShareId(result.shareId);
                setIsLiveShare(true);
                setShowShareModal(true);
                
                // Show expiration info
                const expiresIn = result.expiresAt ? new Date(result.expiresAt) : new Date(Date.now() + 24 * 60 * 60 * 1000);
                const expiryMessage = `✅ Live share created! Expires in 24 hours (${expiresIn.toLocaleString()})`;
                setRouteToast(expiryMessage);
                setTimeout(() => setRouteToast(''), 8000);
            } else {
                // Fallback to base64 encoding
                const encoded = btoa(JSON.stringify(shareData));
                const link = `${window.location.origin}/groupshare/view?data=${encoded}`;
                setShareLink(link);
                setActiveShareId(null);
                setIsLiveShare(false);
                setShowShareModal(true);
                setRouteToast('📋 Static share link created. Regenerate to update.');
                setTimeout(() => setRouteToast(''), 5000);
            }
        } catch (error) {
            console.error('Error generating share link:', error);
            // Fallback to base64 encoded URL
            const shareData = prepareShareData();
            const encoded = btoa(JSON.stringify(shareData));
            const link = `${window.location.origin}/groupshare/view?data=${encoded}`;
            setShareLink(link);
            setActiveShareId(null);
            setIsLiveShare(false);
            setShowShareModal(true);
        }
    };

    const savePriceDataFromTransactions = async () => {
        // Collect price data from sold items
        const priceData = [];
        const commodityNamesSet = new Set();

        haulItems.forEach(item => {
            // Only process items that have been sold (have actualPrice)
            if (item.actualPrice && item.locationId && item.commodityId) {
                const pricePerSCU = Number(item.actualPrice) / (Number(item.quantity) * Number(item.scu));

                priceData.push({
                    commodity_id: item.commodityId,
                    merchant_id: item.locationId,
                    price: pricePerSCU,
                    activity: 'buy', // Merchant is buying from player
                    accepted_scus: [Number(item.scu)], // The SCU size that was sold
                    quantity: Number(item.quantity)
                });

                // PRD-042 Phase 5: Track commodity names for contribution summary
                const commodity = allCommodities.find(c => c.id === item.commodityId);
                if (commodity?.name) {
                    commodityNamesSet.add(commodity.name);
                }
            }
        });

        if (priceData.length > 0) {
            const result = await savePriceDataFromGroupShare(priceData);
            if (result.success) {
                console.log(`Saved ${result.count} price data entries to charts`);
                // PRD-042 Phase 5: Update contribution tracking state
                setSubmittedPricesCount(prev => prev + result.count);
                setSubmittedCommodityNames(prev => [...new Set([...prev, ...commodityNamesSet])]);
                // Show success toast
                setRouteToast(`✅ Price data from ${result.count} sale(s) saved to community charts!`);
                setTimeout(() => setRouteToast(''), 5000);
            } else {
                console.error('Failed to save price data:', result.error);
                setRouteToast(`⚠️ Failed to save price data to charts`);
                setTimeout(() => setRouteToast(''), 5000);
            }
        }
    };

    const handleConfirmSave = async () => {
        if (!selectedOrg || !selectedWallet) return;

        // Premium access check (defensive - should be caught earlier)
        if (!hasPremiumAccess) {
            setOrgSaveError('Save to Org requires premium access');
            return;
        }

        setIsSavingOrg(true);
        setOrgSaveError('');
        try {
            const amount = Number(totalUECEarned || 0);
            if (!amount || amount <= 0) throw new Error('Total amount must be greater than zero.');
            const note = `GroupShare: ${memo ? memo.slice(0, 80) : 'no memo'}`;
            
            // Save price data to charts before saving org transaction
            // Note: Price data persists even if transaction is deleted after 24h for free users
            try {
                const result = await savePriceDataFromTransactions();
                if (result && !result.success) {
                    console.warn('Price data save had issues:', result.error);
                }
            } catch (error) {
                console.error('Failed to save price data:', error);
                // Continue anyway - don't block the transaction
            }
            
            if (splitPerMember) {
                const txs = players.map(p => ({
                    org_id: selectedOrg,
                    wallet_id: selectedWallet,
                    member_id: p.id || null,
                    kind: 'payout',
                    amount: Number((amount * (Number(p.share || 0) / 100)).toFixed(2)),
                    currency: 'aUEC',
                    note,
                    confirmed: true,
                }));
                const { error } = await createOrgTransactionsBulk(txs);
                if (error) throw error;
            } else {
                const { error } = await createOrgTransaction({
                    org_id: selectedOrg,
                    wallet_id: selectedWallet,
                    kind: 'sale',
                    amount,
                    currency: 'aUEC',
                    note,
                    confirmed: true,
                });
                if (error) throw error;
            }
            setShowSaveModal(false);
            setConfirmSave(false);
            setSelectedOrg('');
            setSelectedWallet('');
            alert('Transaction saved to organization ledger.\n\nThe org can review and edit this transaction in their ledger.');
        } catch (e) {
            setOrgSaveError(e.message || 'Failed to save to org');
        } finally {
            setIsSavingOrg(false);
        }
    };

    const getMerchantMajor = (m) => (m && (m.majorPlanet || m.major_planet || m.planetaryBody || m.planetary_body || m.primaryBody || m.primary_body)) || '';
    const normalizeName = (s) => String(s || '').trim().toLowerCase();
    const idEq = (a, b) => String(a) === String(b);

    const getMerchantsForCommodity = (commodityId) => {
        if (!commodityId || !merchantCommodities.length) return [];
        const cid = String(commodityId);
        const relevantMerchantIds = new Set(
            merchantCommodities
                .filter(mc => String(mc.commodity_id) === cid && String(mc.activity).toLowerCase() === 'buy')
                .map(mc => String(mc.merchant_id))
        );
        const sysNorm = normalizeName(selectedSystem);
        const majorNorm = normalizeName(selectedMajorPlanet);
        return allMerchants.filter(merchant => {
            if (!relevantMerchantIds.has(String(merchant.id))) return false;
            if (sysNorm && normalizeName(merchant.system) !== sysNorm) return false;
            if (majorNorm && normalizeName(getMerchantMajor(merchant)) !== majorNorm) return false;
            return true;
        });
    };

    const getLatestBuyPrice = (commodityId, merchantId) => {
        if (!commodityId || !merchantId || !merchantCommodities.length) return 0;
        const merchantCommodity = merchantCommodities.find(mc => 
            idEq(mc.commodity_id, commodityId) && 
            idEq(mc.merchant_id, merchantId) && 
            String(mc.activity).toLowerCase() === 'buy'
        );
        if (!merchantCommodity) return 0;
        if (merchantCommodity.last_buy_price != null && !Number.isNaN(Number(merchantCommodity.last_buy_price))) {
            return Number(merchantCommodity.last_buy_price) || 0;
        }
        if (!merchantCommodity.price_history || merchantCommodity.price_history.length === 0) {
            return 0;
        }
        const lastIdx = merchantCommodity.price_history.length - 1;
        return Number(merchantCommodity.price_history[lastIdx].price) || 0;
    };


    const getAcceptedScuSizes = (commodityId, merchantId) => {
        if (!commodityId || !merchantId || !merchantCommodities.length) return [];
        const merchantCommodity = merchantCommodities.find(mc => 
            idEq(mc.commodity_id, commodityId) && 
            idEq(mc.merchant_id, merchantId) && 
            String(mc.activity).toLowerCase() === 'buy'
        );
        if (!merchantCommodity || !merchantCommodity.accepted_scus) {
            return [];
        }
        return merchantCommodity.accepted_scus
            .map(scu => parseInt(scu, 10))
            .filter(scu => !isNaN(scu))
            .sort((a, b) => a - b);
    };

    const executeMoveToInventory = async (item, storageLocationIdOrKiosk) => {
        if (!item || !storageLocationIdOrKiosk) {
            console.error("No item or location selected for inventory move.");
            return;
        }
    
        const commodity = allCommodities.find(c => c.id === item.commodityId);
        if (!commodity) {
            alert('Commodity details not found.');
            return;
        }
    
        // Resolve whether the provided identifier is a kiosk id or a location id
        let resolvedLocationId = storageLocationIdOrKiosk;
        let resolvedKioskId = '';
        // Prefer item's kiosk if already selected on the row
        if (item.kioskId) {
            resolvedLocationId = item.locationId || resolvedLocationId;
            resolvedKioskId = item.kioskId;
        } else {
            // Try to find a kiosk owner for the provided id
            let owner = null;
            for (const m of allMerchants) {
                const ks = Array.isArray(m.kiosks) ? m.kiosks : [];
                if (ks.find(k => String(k.id) === String(storageLocationIdOrKiosk))) {
                    owner = m;
                    break;
                }
            }
            if (owner) {
                resolvedKioskId = String(storageLocationIdOrKiosk);
                resolvedLocationId = owner.id;
            } else if (typeof storageLocationIdOrKiosk === 'string' && storageLocationIdOrKiosk.includes('::')) {
                // Fallback encoded id format: `${locationId}::index`
                resolvedLocationId = storageLocationIdOrKiosk.split('::')[0];
                resolvedKioskId = storageLocationIdOrKiosk;
            }
        }

        const inventoryItem = {
            commodityId: item.commodityId,
            name: commodity.name,
            quantity: item.quantity,
            scu: item.scu,
            locationId: resolvedLocationId,
            kioskId: resolvedKioskId || null,
        };
    
        try {
            const response = await authFetch('/api/inventory', {
                method: 'POST',
                body: JSON.stringify(inventoryItem)
            }, logout, token);
    
            if (!response.ok) {
                throw new Error('Failed to move item to inventory.');
            }
    
            alert('Item successfully moved to your inventory!');
        } catch (error) {
            console.error("Error moving item to inventory:", error);
            if (error.message.includes('Session expired')) {
                alert('Your session has expired. Please log in again.');
                navigate('/login');
            } else {
                alert('Failed to move item to inventory. Please try again.');
            }
        }
    };

    const handleOpenMoveToInventoryModal = (index) => {
        if (!currentUser) {
            alert('You must be logged in to move items to your inventory.');
            return;
        }
        
        const item = haulItems[index]; // Get the latest item data from state
        if (item.locationId || item.kioskId) {
            executeMoveToInventory(item, item.kioskId || item.locationId);
        } else {
            setSelectedItemForInventory(item);
            setLocationModalOpen(true);
        }
    };

    const handleConfirmMoveToInventory = async (storageLocationId) => {
        if (selectedItemForInventory) {
            await executeMoveToInventory(selectedItemForInventory, storageLocationId);
        }
        setLocationModalOpen(false);
        setSelectedItemForInventory(null);
    };

    const handleHaulItemChange = async (index, event) => {
        const { name, value } = event.target;
        const newHaulItems = [...haulItems];
        const currentItem = { ...newHaulItems[index] };
        if (name === 'quantity') {
            const v = Math.max(0, Number(value) || 0);
            currentItem[name] = v;
        } else if (name === 'scu') {
            const v = Math.max(0, Number(value) || 0);
            currentItem[name] = v;
        } else {
            currentItem[name] = value;
        }

        if (name === 'commodityId') {
            currentItem.locationId = '';
            currentItem.kioskId = '';
            currentItem.scu = 1;
            // Populate avgPrice with market average SELL price per SCU (acquisition baseline)
            // Fallback to BUY averages only if SELL data unavailable
            const fallbackAvg = Number(
                (marketAvgSellByCommodity[value] || avgSellByCommodityFallback[value] ||
                 marketAvgByCommodity[value] || avgByCommodityFallback[value] || 0)
            );
            currentItem.avgPrice = fallbackAvg;
            currentItem.totalUEC = 0;
        }
        if (name === 'locationId') {
            currentItem.scu = 1;
            currentItem.kioskId = '';
        }
        if ((name === 'commodityId' || name === 'locationId') && currentItem.commodityId && currentItem.locationId) {
            const sellPricePerSCU = getLatestBuyPrice(currentItem.commodityId, currentItem.locationId);
            // When a location is selected, use the merchant BUY price as expected sale price per SCU
            currentItem.avgPrice = sellPricePerSCU;
            const acceptedScus = getAcceptedScuSizes(currentItem.commodityId, currentItem.locationId);
            if (acceptedScus.length > 0 && !acceptedScus.includes(currentItem.scu)) {
                currentItem.scu = acceptedScus[0];
            }
        }
        // Always recompute total when any relevant field changes
        currentItem.totalUEC = Number(currentItem.quantity || 0) * Number(currentItem.scu || 0) * Number(currentItem.avgPrice || 0);
        newHaulItems[index] = currentItem;
        setHaulItems(newHaulItems);
    };

    const duplicateHaulItem = (index) => {
        const src = haulItems[index];
        if (!src) return;
        const copy = { ...src, actualPrice: '' };
        const next = [...haulItems];
        next.splice(index + 1, 0, copy);
        setHaulItems(next);
    };

    const handleCopyPayouts = async () => {
        try {
            const lines = players.map(p => {
                const playerShareValue = Number(totalUECEarned || 0) * (Number(p.share || 0) / 100);
                const net = playerShareValue - (playerShareValue * 0.0025);
                return `${p.name || 'Unknown'}: ${net.toFixed(2)} aUEC`;
            }).join('\n');
            if (navigator.clipboard && navigator.clipboard.writeText) {
                await navigator.clipboard.writeText(lines);
            } else {
                const ta = document.createElement('textarea');
                ta.value = lines;
                ta.setAttribute('readonly', '');
                ta.style.position = 'absolute';
                ta.style.left = '-9999px';
                document.body.appendChild(ta);
                ta.select();
                document.execCommand('copy');
                document.body.removeChild(ta);
            }
            alert('Payout breakdown copied to clipboard.');
        } catch (_) {
            alert('Failed to copy payouts.');
        }
    };

    const handleExportCsv = () => {
        try {
            const rows = [];
            // Haul items
            (haulItems || []).forEach(item => {
                const commodityName = allCommodities.find(c => c.id === item.commodityId)?.name || 'Unknown';
                const locationName = allMerchants.find(m => m.id === item.locationId)?.name || 'Unknown';
                rows.push({
                    type: 'haul', commodityName, locationName,
                    quantity: Number(item.quantity || 0), scu: Number(item.scu || 0),
                    avgPrice: Number(item.avgPrice || 0), totalUEC: Number(item.totalUEC || 0), actualPrice: Number(item.actualPrice || 0)
                });
            });
            // Line items
            (lineItems || []).forEach(li => {
                rows.push({ type: 'earning', category: li.category || '', amount: Number(li.amount || 0), notes: li.notes || '' });
            });
            // Players
            (players || []).forEach(p => {
                const playerShareValue = Number(totalUECEarned || 0) * (Number(p.share || 0) / 100);
                const fee = playerShareValue * 0.0025;
                const net = playerShareValue - fee;
                rows.push({ type: 'player', name: p.name || '', share: Number(p.share || 0), playerShare: playerShareValue.toFixed(2), transactionFee: fee.toFixed(2), netPlayerShare: net.toFixed(2), paid: !!p.paid, notify: !!p.notify });
            });
            const headers = Array.from(new Set(rows.flatMap(r => Object.keys(r))));
            const csvRows = [headers.join(',')].concat(rows.map(r => headers.map(h => {
                const v = r[h];
                if (v === undefined || v === null) return '';
                const s = String(v).replace(/"/g, '""');
                return /[",\n]/.test(s) ? `"${s}"` : s;
            }).join(',')));
            
            // Add footer with thank you message
            csvRows.push(''); // Empty row for spacing
            csvRows.push(''); // Another empty row
            csvRows.push('"Thanks for using The Orbital Ledger — your co-pilot in trading the verse."');
            
            const csv = csvRows.join('\n');
            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            const dt = new Date();
            const pad = n => String(n).padStart(2, '0');
            const stamp = `${dt.getFullYear()}${pad(dt.getMonth()+1)}${pad(dt.getDate())}-${pad(dt.getHours())}${pad(dt.getMinutes())}`;
            a.href = url;
            a.download = `groupshare-${stamp}.csv`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (_) {
            alert('Failed to export CSV.');
        }
    };

    const restoreDraft = () => {
        try {
            const raw = localStorage.getItem(draftKey);
            if (!raw) { setShowDraftBanner(false); return; }
            const d = JSON.parse(raw);
            if (d) {
                if (Array.isArray(d.haulItems) && d.haulItems.length) setHaulItems(d.haulItems);
                if (Array.isArray(d.lineItems)) setLineItems(d.lineItems);
                if (Array.isArray(d.players) && d.players.length) setPlayers(d.players);
                if (typeof d.memo === 'string') setMemo(d.memo);
            }
        } catch (_) {}
        setShowDraftBanner(false);
    };
    const discardDraft = () => {
        try { localStorage.removeItem(draftKey); } catch (_) {}
        setShowDraftBanner(false);
    };

    const addHaulItem = () => {
        startTransition(() => {
            setHaulItems([...haulItems, { ...defaultHaulItem, id: Date.now() + Math.random() }]);
        });
    };

    const removeHaulItem = (index) => {
        startTransition(() => {
            setHaulItems(haulItems.filter((_, i) => i !== index));
        });
    };

    const queryUsernames = async (q) => {
        try {
            const res = await authFetch(`/api/ledger/usernames?q=${encodeURIComponent(q)}`, {}, logout, token);
            if (!res.ok) return [];
            const data = await res.json();
            try { console.log('[GS autocomplete]', q, 'results:', Array.isArray(data) ? data.length : 0); } catch(_){}
            return Array.isArray(data) ? data : [];
        } catch (_) { return []; }
    };

    const handlePlayerChange = async (index, event) => {
        const { name, value } = event.target;
        const newPlayers = [...players];
        if (name === 'share') {
            const num = Math.max(0, Math.min(100, Number(value) || 0));
            newPlayers[index][name] = Math.round(num * 100) / 100;
        } else if (name === 'sc_player_handle') {
            // Map sc_player_handle input to player.name state
            newPlayers[index].name = value;
        } else {
            newPlayers[index][name] = value;
        }
        setPlayers(newPlayers);

        if (name === 'sc_player_handle') {
            setNameSuggestIdx(index);
            const list = await queryUsernames(value);
            setNameSuggestions(list);
            // Reset notify flag when changing name
            newPlayers[index].notify = false;
            setPlayers([...newPlayers]);
        }
    };

    const selectSuggestedName = (index, suggestion) => {
        const newPlayers = [...players];
        newPlayers[index].name = suggestion.username || '';
        newPlayers[index].id = suggestion.id;
        newPlayers[index].notify = true; // default to notify if we matched a profile
        setPlayers(newPlayers);
        setNameSuggestIdx(null);
        setNameSuggestions([]);
    };

    const handlePlayerPaidChange = (index, isChecked) => {
        const newPlayers = [...players];
        newPlayers[index].paid = isChecked;
        setPlayers(newPlayers);
    };

    const addPlayer = () => {
        startTransition(() => {
            setPlayers([...players, { name: '', share: 0, paid: false, id: null }]);
        });
    };

    const removePlayer = (index) => {
        startTransition(() => {
            setPlayers(players.filter((_, i) => i !== index));
        });
    };

    const handleCopy = (text, index) => {
        const doMark = () => { setCopiedIndex(index); setTimeout(() => setCopiedIndex(null), 1500); };
        try {
            if (navigator.clipboard && navigator.clipboard.writeText) {
                navigator.clipboard.writeText(text).then(doMark).catch(() => {
                    const ta = document.createElement('textarea');
                    ta.value = text; ta.setAttribute('readonly', ''); ta.style.position = 'absolute'; ta.style.left = '-9999px';
                    document.body.appendChild(ta); ta.select(); document.execCommand('copy'); document.body.removeChild(ta);
                    doMark();
                });
            } else {
                const ta = document.createElement('textarea');
                ta.value = text; ta.setAttribute('readonly', ''); ta.style.position = 'absolute'; ta.style.left = '-9999px';
                document.body.appendChild(ta); ta.select(); document.execCommand('copy'); document.body.removeChild(ta);
                doMark();
            }
        } catch (err) {
            console.error('Could not copy text: ', err);
        }
    };

    const haulTotalUEC = useMemo(() => {
        return haulItems.reduce((acc, item) => {
            const priceToUse = Number(item.actualPrice) > 0 ? Number(item.actualPrice) : Number(item.totalUEC);
            return acc + priceToUse;
        }, 0);
    }, [haulItems]);

    const totalLineItems = useMemo(() => {
        return (lineItems || []).reduce((acc, li) => acc + Number(li.amount || 0), 0);
    }, [lineItems]);
    const totalUECEarned = useMemo(() => {
        return haulTotalUEC + totalLineItems;
    }, [haulTotalUEC, totalLineItems]);
    
    const totalPlayerSharePercentage = useMemo(() => {
        return players.reduce((acc, p) => acc + Number(p.share), 0);
    }, [players]);

    const isSaveDisabled = useMemo(() => {
        const allPlayersPaid = players.every(p => p.paid);
        const totalShareIs100 = Math.abs(totalPlayerSharePercentage - 100) < 0.01;
        return !allPlayersPaid || !totalShareIs100;
    }, [players, totalPlayerSharePercentage]);


    // Friendly validation messages for Save/Share
    const saveIssues = useMemo(() => {
        const issues = [];
        const allPaid = players.every(p => p.paid);
        const shareIs100 = Math.abs(totalPlayerSharePercentage - 100) < 0.01;
        if (!allPaid) issues.push('Mark all players Paid');
        if (!shareIs100) issues.push('Shares must total 100%');
        return issues;
    }, [players, totalPlayerSharePercentage]);

    // Auto-update live share when data changes
    useEffect(() => {
        if (!activeShareId || !isLiveShare) return;
        
        const updateShare = async () => {
            // Calculate profit data inline to avoid stale closures
            const acquisitionCost = haulItems.reduce((sum, item) => {
                const qty = Number(item.quantity || 0);
                const scu = Number(item.scu || 0);
                const avgPrice = Number(item.avgPrice || 0);
                return sum + (qty * scu * avgPrice);
            }, 0);
            
            const revenue = totalUECEarned || 0;
            const profit = revenue - acquisitionCost;
            const margin = acquisitionCost > 0 ? ((profit / acquisitionCost) * 100).toFixed(2) : 0;
            
            const shareData = {
                haulItems: haulItems.map(item => ({
                    commodityId: item.commodityId,
                    commodityName: allCommodities.find(c => c.id === item.commodityId)?.name || '',
                    locationId: item.locationId,
                    locationName: allMerchants.find(m => m.id === item.locationId)?.name || '',
                    quantity: item.quantity,
                    scu: item.scu,
                    avgPrice: item.avgPrice,
                    totalUEC: item.totalUEC,
                    actualPrice: item.actualPrice,
                    leftover: item.leftover
                })),
                routeMode: routeMode,
                currentLocation: {
                    system: selectedSystem,
                    planet: selectedMajorPlanet
                },
                players: players.map(p => ({
                    name: p.name,
                    share: p.share,
                    paid: p.paid
                })),
                totalAcquisitionCost: acquisitionCost,
                totalUECEarned: revenue,
                totalProfit: profit,
                profitMargin: margin,
                memo: memo,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
                createdBy: currentUser?.username || 'Anonymous'
            };
            
            console.log('Updating live share:', activeShareId, 'with', haulItems.length, 'items');
            const result = await updateGroupShareLink(activeShareId, shareData);
            if (result.success) {
                console.log('✅ Live share updated successfully');
                setRouteToast('✅ Share updated');
                setTimeout(() => setRouteToast(''), 1000);
            } else {
                console.error('❌ Failed to update live share');
            }
        };
        
        // Debounce updates to avoid too many API calls
        const timer = setTimeout(updateShare, 2000);
        return () => clearTimeout(timer);
    }, [haulItems, players, memo, selectedSystem, selectedMajorPlanet, routeMode, activeShareId, isLiveShare, totalUECEarned, allCommodities, allMerchants, currentUser]);

    const resetAll = () => {
        startTransition(() => {
            setHaulItems([defaultHaulItem]);
            setPlayers([defaultPlayer]);
            setMemo('');
            setMiscEarnings('');
            setLineItems([{ category: 'Trade', amount: '', notes: '' }]);
        });
    };

    const saveToDatabase = async (options = {}) => {
        console.log('saveToDatabase called with options:', options);
        
        // Check for validation issues
        if (isSaveDisabled && !options.allowUnpaid) {
            alert('Cannot save. Please ensure all players are marked as "Paid" and the total share is 100%.');
            return;
        }
        
        // Check if there are party members to notify
        const notifiablePlayers = players.filter(p => p && p.id && p.notify);
        const hasPartyMembers = notifiablePlayers.length > 0;
        
        // If party members exist and not explicitly skipping prompt, ask user
        if (hasPartyMembers && !options.skipPartyPrompt) {
            const playerNames = notifiablePlayers.map(p => p.name || 'Unknown').join(', ');
            const shouldNotify = window.confirm(
                `Do you want to send this transaction to users in your party?\n\n` +
                `Party members: ${playerNames}\n\n` +
                `They will receive a notification to add funds to their wallet.`
            );
            
            if (!shouldNotify) {
                // User chose not to notify, remove notify flags temporarily for this save
                options.skipNotifications = true;
            }
        }
        
        setIsSaving(true);

        const priceReportPromises = haulItems.map(item => {
            // Skip if no commodity, no location, or actualPrice is empty/invalid
            const actualPriceValue = item.actualPrice ? parseFloat(item.actualPrice) : 0;
            
            if (!item.commodityId) {
                // Silently skip - this is expected for empty items
                return null;
            }
            if (!item.locationId || item.locationId === "") {
                // Silently skip - user hasn't selected location yet
                return null;
            }
            if (!item.actualPrice || item.actualPrice === "" || actualPriceValue <= 0) {
                // This is expected when using estimated prices
                console.log("Price report skipped - using estimated price");
                return null;
            }
            const denom = (Number(item.quantity) || 0) * (Number(item.scu) || 0);
            const pricePerScu = denom > 0 ? (parseFloat(item.actualPrice) / denom) : 0;
            
            const priceReport = {
                merchantId: item.locationId,
                commodityId: item.commodityId,
                price: pricePerScu,
                activity: 'sell',
                userId: currentUser ? currentUser.id : 'anonymous',
                scu: Number(item.scu) || null
            };

            return fetch('/api/prices', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
                body: JSON.stringify(priceReport)
            });
        }).filter(promise => promise !== null);

        try {
            const priceResponses = await Promise.all(priceReportPromises);
            let okCount = 0;
            priceResponses.forEach(async (res, index) => {
                if (!res.ok) {
                    const errorData = await res.json();
                    console.error(`Failed to report price for item ${index}:`, errorData.message);
                } else {
                    okCount += 1;
                }
            });
            console.log("All prices reported successfully.");
            if (okCount > 0) {
                try { setRouteToast(`${okCount} price update${okCount!==1?'s':''} submitted for charts.`); } catch(_) {}
            }
        } catch (error) {
            console.error("An error occurred while reporting prices:", error);
        }

        const playersWithIds = players.map((player, idx) => {
            const finalPlayer = { ...player };
            // Match self by username or by existing ID
            if (currentUser && (player.name === currentUser.username || player.id === currentUser.id)) {
                finalPlayer.id = currentUser.id;
            // Solo mode: if only 1 player and no ID set, assume it's the current user
            } else if (currentUser && players.length === 1 && !player.id) {
                finalPlayer.id = currentUser.id;
            } else {
                finalPlayer.id = finalPlayer.id || null;
            }
            return finalPlayer;
        });

        const transactionData = {
            userId: currentUser ? currentUser.id : 'anonymous',
            haulItems: haulItems.map(item => ({
                commodityId: item.commodityId,
                commodityName: allCommodities.find(c => c.id === item.commodityId)?.name || 'Unknown',
                locationId: item.locationId,
                kioskId: item.kioskId || null,
                locationName: allMerchants.find(m => m.id === item.locationId)?.name || 'Unknown',
                quantity: item.quantity,
                scu: item.scu,
                avgPrice: item.avgPrice,
                totalUEC: item.totalUEC,
                actualPrice: item.actualPrice 
            })),
            lineItems: (lineItems || []).map(li => ({ category: li.category, amount: Number(li.amount || 0), notes: li.notes || '' })),
            players: playersWithIds.map(player => {
                const playerShareValue = totalUECEarned * (player.share / 100);
                const transactionFee = playerShareValue * 0.0025;
                const netPlayerShare = playerShareValue - transactionFee;
                
                // Skip notifications if requested
                const shouldNotify = options.skipNotifications ? false : player.notify;
                
                console.log('[GroupShare] Player data:', {
                    name: player.name,
                    id: player.id,
                    notify: player.notify,
                    shouldNotify: shouldNotify,
                    netPlayerShare: netPlayerShare.toFixed(2)
                });
                
                return {
                    ...player,
                    id: player.id || null,
                    playerShare: playerShareValue.toFixed(2),
                    transactionFee: transactionFee.toFixed(2),
                    netPlayerShare: netPlayerShare.toFixed(2),
                    notify: shouldNotify
                }
            }),
            miscEarnings: miscEarnings,
            totalUECEarned: totalUECEarned.toFixed(2),
            memo: memo,
            tier: hasPremiumAccess ? 'paid' : 'free',
        };
        
        try {
            console.log('Sending transaction data to API:', transactionData);
            const response = await authFetch(transactionId ? `/api/ledger/${transactionId}` : '/api/ledger', {
                method: transactionId ? 'PUT' : 'POST',
                body: JSON.stringify(transactionData),
            }, logout, token);
            
            console.log('API response status:', response.status);
            const data = await response.json();
            console.log('API response data:', data);

            if (!response.ok) {
                throw new Error(data.message || `Failed to ${transactionId ? 'update' : 'save'} ledger entry.`);
            }

            const savedTransactionId = data.id || transactionId;
            
            // Build success message based on what happened
            let successMessage = 'Transaction saved to ledger.';
            
            // Use the players array from transactionData which has the calculated shares
            const playersWithShares = transactionData.players;
            
            // Process self-allocation to wallet
            const selfPlayer = playersWithShares.find(p => p.id === currentUser?.id);
            console.log('Self player check:', { 
                selfPlayer, 
                hasPremiumAccess, 
                currentUserId: currentUser?.id 
            });
            
            if (selfPlayer && selfPlayer.paid && hasPremiumAccess) {
                try {
                    // Add to wallet
                    const walletEntry = {
                        category: 'Trade',
                        amount: parseFloat(selfPlayer.netPlayerShare),
                        notes: `Group share payout from transaction ${savedTransactionId}`,
                        transactionId: savedTransactionId
                    };
                    
                    console.log('Sending wallet entry:', walletEntry);
                    
                    const walletResponse = await authFetch('/api/ledger/wallet', {
                        method: 'POST',
                        body: JSON.stringify(walletEntry)
                    }, logout, token);
                    
                    if (walletResponse.ok) {
                        const walletData = await walletResponse.json();
                        console.log('Wallet updated successfully:', walletData);
                        successMessage += '\n✓ Your share has been added to your wallet.';
                    } else {
                        const errorText = await walletResponse.text();
                        console.error('Wallet response not ok:', errorText);
                        successMessage += '\n✓ Your share will be tracked in the transaction history.';
                    }
                } catch (error) {
                    console.error('Failed to add to wallet:', error);
                    successMessage += '\n✓ Your share will be tracked in the transaction history.';
                }
            }
            
            // Send notifications to party members
            const notifiedPlayers = playersWithShares.filter(p => {
                const actualNotify = options.skipNotifications ? false : p.notify;
                return actualNotify && p.id && p.id !== currentUser?.id;
            });
            
            console.log('Notified players:', notifiedPlayers);
            
            // NOTE: Notifications are now handled by the backend when creating the transaction
            // The backend creates wallet entries and sends notifications automatically
            // This frontend code is commented out to avoid duplicate notifications
            /*
            if (notifiedPlayers.length > 0) {
                try {
                    // Send notifications to each party member
                    const notificationPromises = notifiedPlayers.map(async (player) => {
                        const notificationData = {
                            recipientId: player.id,
                            type: 'group_share_payout',
                            message: `${currentUser?.username || 'A player'} has sent you ${player.netPlayerShare} UEC from a group share. Would you like to add it to your wallet?`,
                            context: {
                                transactionId: savedTransactionId,
                                amount: player.netPlayerShare,
                                senderName: currentUser?.username || 'Unknown',
                                playerShare: player.playerShare,
                                transactionFee: player.transactionFee
                            }
                        };
                        
                        console.log('Sending notification:', notificationData);
                        
                        const response = await authFetch('/api/auth/notifications', {
                            method: 'POST',
                            body: JSON.stringify(notificationData)
                        }, logout, token);
                        
                        if (!response.ok) {
                            const errorText = await response.text();
                            console.error('Notification failed for', player.name, ':', errorText);
                        } else {
                            console.log('Notification sent successfully to', player.name);
                        }
                        
                        return response;
                    });
                    
                    await Promise.all(notificationPromises);
                    successMessage += `\n✓ ${notifiedPlayers.length} party member${notifiedPlayers.length !== 1 ? 's' : ''} notified to claim funds.`;
                } catch (error) {
                    console.error('Failed to send notifications:', error);
                    successMessage += `\n✓ ${notifiedPlayers.length} party member${notifiedPlayers.length !== 1 ? 's' : ''} will be tracked in the transaction.`;
                }
            }
            */
            
            // Update success message to reflect that notifications were sent by backend
            if (notifiedPlayers.length > 0) {
                successMessage += `\n✓ ${notifiedPlayers.length} party member${notifiedPlayers.length !== 1 ? 's' : ''} notified to claim funds.`;
            }
            
            // Add expiry warning for free users
            if (!hasPremiumAccess) {
                successMessage += '\n\n⚠️ Free account: Transaction history retained for 24 hours only.';
            }
            
            alert(successMessage);

            if (!options.skipReset) {
                if (!transactionId) {
                    resetAll();
                }
            }
        } catch (error) {
            console.error('Error saving to database:', error);
            alert(`Error: ${error.message}`);
        } finally {
            setIsSaving(false);
        }
    };

    // eslint-disable-next-line no-unused-vars
    const _systemOptions = useMemo(() => {
        const systems = allMerchants.map(m => m.system).filter(Boolean);
        return Array.from(new Set(systems));
    }, [allMerchants]);
    // eslint-disable-next-line no-unused-vars
    const _majorPlanetOptions = useMemo(() => {
        return Array.from(new Set(
            allMerchants
                .filter(m => m.system === selectedSystem)
                .map(m => getMerchantMajor(m))
                .filter(Boolean)
        ));
    }, [allMerchants, selectedSystem]);

    const routeSuggestions = useMemo(() => {
        if (!deferredSystem || !deferredMajor || deferredHaulItems.length === 0) return [];
        const sysNorm = normalizeName(deferredSystem);
        const majorNorm = normalizeName(deferredMajor);
        const findMerchantCommodity = (merchantId, commodityId) => {
            // We are selling cargo to merchants, so we need merchants' BUY prices
            return merchantCommodities.find(mc => idEq(mc.merchant_id, merchantId) && idEq(mc.commodity_id, commodityId) && String(mc.activity).toLowerCase() === 'buy');
        };
        const latestPrice = (mc) => {
            if (!mc) return 0;
            if (mc.last_buy_price != null && !Number.isNaN(Number(mc.last_buy_price))) return Number(mc.last_buy_price) || 0;
            const ph = mc.price_history || [];
            if (ph.length === 0) return 0;
            return Number(ph[ph.length - 1]?.price) || 0;
        };
        const acceptsScu = (mc, scu) => {
            const arr = mc && Array.isArray(mc.accepted_scus) ? mc.accepted_scus : [];
            if (!arr || arr.length === 0) return true;
            const n = Number(scu || 0);
            return arr.some(x => Number(x) === n);
        };
        let list = deferredHaulItems.map((item, idx) => {
            if (!item || !item.commodityId) return null;
            if ((Number(item.quantity) || 0) <= 0) return null;
            if (Number(item.actualPrice || 0) > 0) return null; // skip already sold
            const merchantsWithBuy = deferredAllMerchants.filter(m => {
                const mc = findMerchantCommodity(m.id, item.commodityId);
                if (!mc) return false;
                if (!acceptsScu(mc, item.scu)) return false;
                const p = latestPrice(mc) || Number((recentBuyMerchantsMap[String(item.commodityId)] || {})[String(m.id)] || 0);
                if (!p || p <= 0) return false;
                // For fastSell, restrict to current system; for bestPrice, allow all systems
                if (routeMode !== 'bestPrice' && normalizeName(m.system) !== sysNorm) return false;
                return true;
            });
            if (merchantsWithBuy.length === 0) return null;
            let suggestion = null;
            if (routeMode === 'fastSell') {
                // Prefer current location -> same major -> same system -> any
                let pool = merchantsWithBuy.filter(m => normalizeName(getMerchantMajor(m)) === majorNorm);
                if (pool.length === 0) pool = merchantsWithBuy; // already system-scoped for fastSell
                // rank within pool by latestPrice snapshot, then fallback to recent buy price history if equal/missing
                let best = null;
                let bestPrice = -Infinity;
                pool.forEach(m => {
                    const mc = findMerchantCommodity(m.id, item.commodityId);
                    let price = latestPrice(mc);
                    if (!price || price <= 0) {
                        const byMerch = recentBuyMerchantsMap[String(item.commodityId)] || {};
                        const alt = Number(byMerch[String(m.id)] || 0);
                        if (alt && alt > 0) price = alt;
                    }
                    if (price > bestPrice) { best = m; bestPrice = price; }
                });
                suggestion = best;
            } else {
                let best = null;
                let bestPrice = -Infinity;
                merchantsWithBuy.forEach(m => {
                    const mc = findMerchantCommodity(m.id, item.commodityId);
                    const price = latestPrice(mc) || Number((recentBuyMerchantsMap[String(item.commodityId)] || {})[String(m.id)] || 0);
                    if (price > bestPrice) { best = m; bestPrice = price; }
                });
                suggestion = best;
            }
            if (!suggestion) return null;
            const mc = findMerchantCommodity(suggestion.id, item.commodityId);
            const price = latestPrice(mc);
            // Acquisition cost per SCU for profitability: prefer item.avgPrice (set from Stage 1),
            // then fall back to market SELL averages, then market BUY averages.
            const acquisitionPerScu = Number(
              (item && item.avgPrice) ||
              marketAvgSellByCommodity[item.commodityId] || avgSellByCommodityFallback[item.commodityId] ||
              marketAvgByCommodity[item.commodityId] || avgByCommodityFallback[item.commodityId] || 0
            );
            return {
                index: idx,
                itemName: deferredAllCommodities.find(c => c.id === item.commodityId)?.name || 'Unknown',
                merchantName: suggestion.name,
                location: `${suggestion.system} / ${getMerchantMajor(suggestion) || ''} / ${suggestion.planetaryBody || ''}`,
                price,
                merchantId: suggestion.id,
                quantity: Number(item.quantity || 0),
                scu: Number(item.scu || 0),
                estTotal: Number(price) * Number(item.quantity || 0) * Number(item.scu || 0),
                estProfit: Math.max(0, (Number(price) - Number(acquisitionPerScu))) * Number(item.quantity || 0) * Number(item.scu || 0),
                // rank fields
                profitPerUnit: Math.max(0, Number(price) - Number(acquisitionPerScu)),
                distanceRank: getMerchantMajor(suggestion) === deferredMajor ? 0 : 1,
                _mode: routeMode
            };
        }).filter(Boolean);
        // sort according to mode
        if (routeMode === 'bestPrice') {
            list.sort((a, b) => (Number(b.estProfit) || 0) - (Number(a.estProfit) || 0));
        } else {
            list.sort((a, b) => (a.distanceRank || 0) - (b.distanceRank || 0));
        }
        return list;
    }, [
        deferredHaulItems,
        deferredAllMerchants,
        deferredAllCommodities,
        deferredSystem,
        deferredMajor,
        routeMode,
        // pricing sources actually used inside this memo
        merchantCommodities,
        marketAvgSellByCommodity,
        avgSellByCommodityFallback,
        marketAvgByCommodity,
        avgByCommodityFallback,
        recentSellMerchantsMap
    ]);
    const routeSteps = useMemo(() => {
        if (!routeSuggestions || routeSuggestions.length === 0) return [];
        const byMerchant = new Map();
        for (const s of routeSuggestions) {
            const key = s.merchantId;
            if (!byMerchant.has(key)) byMerchant.set(key, { merchantId: key, merchantName: s.merchantName, location: s.location, items: [], estTotal: 0, estProfit: 0 });
            const g = byMerchant.get(key);
            g.items.push(s);
            g.estTotal += Number(s.estTotal || 0);
            g.estProfit += Number(s.estProfit || 0);
        }
        let steps = Array.from(byMerchant.values());
        if (routeMode === 'bestPrice') steps.sort((a,b) => (b.estProfit||0) - (a.estProfit||0));
        else steps.sort((a,b) => {
            const ar = Math.min(...a.items.map(i => i.distanceRank || 0));
            const br = Math.min(...b.items.map(i => i.distanceRank || 0));
            return ar - br;
        });
        return steps;
    }, [routeSuggestions, routeMode]);

    // Ordered Route Steps (drag to reorder)
    const [routeOrder, setRouteOrder] = useState([]);
    const lastRouteModeRef = useRef(routeMode);
    const lastSystemRef = useRef(selectedSystem);
    const lastMajorRef = useRef(selectedMajorPlanet);
    useEffect(() => {
        try {
            const ids = (routeSteps || []).map(s => s.merchantId);
            setRouteOrder(prev => {
                const modeChanged = lastRouteModeRef.current !== routeMode;
                const systemChanged = lastSystemRef.current !== selectedSystem;
                const majorChanged = lastMajorRef.current !== selectedMajorPlanet;
                lastRouteModeRef.current = routeMode;
                lastSystemRef.current = selectedSystem;
                lastMajorRef.current = selectedMajorPlanet;
                // When switching modes, reset to the freshly computed order so the UI reflects the mode
                if (modeChanged || systemChanged || majorChanged) return ids;
                if (!prev || prev.length === 0) return ids;
                const keep = prev.filter(id => ids.includes(id));
                const add = ids.filter(id => !keep.includes(id));
                return keep.concat(add);
            });
        } catch(_) {}
    }, [routeSteps, routeMode, selectedSystem, selectedMajorPlanet]);
    const orderedRouteSteps = useMemo(() => {
        if (!routeOrder || routeOrder.length === 0) return routeSteps;
        const pos = new Map(routeOrder.map((id, i) => [id, i]));
        return [...routeSteps].sort((a, b) => (pos.get(a.merchantId) ?? 1e9) - (pos.get(b.merchantId) ?? 1e9));
    }, [routeSteps, routeOrder]);
    // Persist route order per selected system/major planet AND mode
    const routeOrderKey = useMemo(() => `gs_route_order_${selectedSystem || 'any'}_${selectedMajorPlanet || 'any'}_${routeMode}`, [selectedSystem, selectedMajorPlanet, routeMode]);

    useEffect(() => {
        try {
            const raw = localStorage.getItem(routeOrderKey);
            if (!raw) return;
            const ids = JSON.parse(raw);
            if (!Array.isArray(ids) || ids.length === 0) return;
            // Only keep ids that exist in current steps
            const valid = ids.filter(id => (routeSteps || []).some(s => s.merchantId === id));
            if (valid.length > 0) setRouteOrder(valid);
        } catch(_) {}
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [routeSteps, routeOrderKey]);
    useEffect(() => {
        try { localStorage.setItem(routeOrderKey, JSON.stringify(routeOrder || [])); } catch(_) {}
    }, [routeOrder, routeOrderKey]);


    const applyRouteSuggestion = (index, merchantId, price) => {
        const next = [...haulItems];
        const it = { ...next[index] };
        it.locationId = merchantId;
        it.avgPrice = Number(price) || 0;
        // Adjust SCU if needed for merchant acceptance
        const accepted = getAcceptedScuSizes(it.commodityId, merchantId);
        if (accepted.length > 0 && !accepted.includes(Number(it.scu))) {
            it.scu = accepted[0];
        }
        it.totalUEC = Number(it.avgPrice || 0) * Number(it.quantity || 0) * Number(it.scu || 0);
        next[index] = it;
        setHaulItems(next);
    };

    // Route mode toast (flavor text)
    const [routeToast, setRouteToast] = useState(null);
    const lastRouteToastKey = useRef('');

    const buildRouteToastText = (mode, suggestions) => {
        const count = suggestions.length;
        const total = suggestions.reduce((acc, s) => acc + (Number(s.estTotal || 0)), 0);
        if (mode === 'fastSell') {
            const s0 = suggestions[0];
            return `Here's the fastest route to sell your cargo. Quick hop to ${s0.merchantName} (${s0.location}). Estimated earnings: ${total.toFixed(0)} aUEC across ${count} line item${count !== 1 ? 's' : ''}.`;
        }
        // bestPrice
        let top = suggestions[0];
        for (const s of suggestions) {
            if ((Number(s.estProfit) || 0) > (Number(top.estProfit) || 0)) top = s;
        }
        const profit = suggestions.reduce((acc, s) => acc + (Number(s.estProfit || 0)), 0);
        return `Best profits detected. Prime sale at ${top.merchantName} (${top.location}). Potential take: ${total.toFixed(0)} aUEC (est. profit +${profit.toFixed(0)} aUEC) across ${count} line item${count !== 1 ? 's' : ''}.`;
    };

    useEffect(() => {
        const key = `${routeMode}|${selectedSystem}|${selectedMajorPlanet}|${routeSuggestions.length}`;
        if (routeSuggestions.length > 0 && key !== lastRouteToastKey.current) {
            setRouteToast(buildRouteToastText(routeMode, routeSuggestions));
            lastRouteToastKey.current = key;
        }
        // Auto-apply suggestions when switching modes if user requested
        if (pendingAutoApply && routeSuggestions.length > 0) {
            const hasDiff = routeSuggestions.some(s => {
                const it = haulItems[s.index];
                if (!it) return true;
                if (pendingAutoApply === 'missing' && it.locationId) return false;
                return (String(it.locationId||'') !== String(s.merchantId||'')) || (Number(it.avgPrice||0) !== Number(s.price||0));
            });
            if (hasDiff) {
                if (pendingAutoApply === 'all') {
                    routeSuggestions.forEach(s => applyRouteSuggestion(s.index, s.merchantId, s.price));
                } else if (pendingAutoApply === 'missing') {
                    routeSuggestions.forEach(s => {
                        const it = haulItems[s.index];
                        if (!it || it.locationId) return;
                        applyRouteSuggestion(s.index, s.merchantId, s.price);
                    });
                }
                setPendingAutoApply('');
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [routeMode, routeSuggestions, haulItems]);

    useEffect(() => {
        if (!routeToast) return;
        const t = setTimeout(() => setRouteToast(null), 5000);
        return () => clearTimeout(t);
    }, [routeToast]);

    // Free tier expiry banner (client-side countdown)
    const [expiryCountdown, setExpiryCountdown] = useState(null);
    useEffect(() => {
        if (!transactionId || hasPremiumAccess) return;
        // If editing a free-tier saved txn, show countdown based on createdAt
        (async () => {
            try {
                const res = await fetch(`/api/ledger/${transactionId}`);
                if (!res.ok) return;
                const txn = await res.json();
                const created = new Date(txn.createdAt || txn.date || Date.now()).getTime();
                const end = created + 24*60*60*1000;
                const update = () => setExpiryCountdown(Math.max(0, end - Date.now()));
                update();
                const t = setInterval(update, 1000);
                return () => clearInterval(t);
            } catch(_) {}
        })();
    }, [transactionId, hasPremiumAccess]);

    return (
        <div className="page-container group-share-page">
            <h1 className="page-title">{transactionId ? 'Edit Transaction' : 'Group Share Calculator'}</h1>
            <p className="page-subtitle">Plan routes, record sells, and split payouts.</p>
            {!hasPremiumAccess && currentUser && (
                <>
                    <div className="groupshare-expiry-banner">
                        {transactionId ? (
                            <>
                              This GroupShare will be saved for 24 hours only.
                              {expiryCountdown !== null && (
                                <span className="expiry-timer"> · Expires in {Math.ceil(expiryCountdown/1000)}s</span>
                              )}
                            </>
                        ) : (
                            <>Free plan: Saved GroupShare entries will auto-delete after 24 hours.</>
                        )}
                        <Link to="/purchase" className="upgrade-link">Upgrade now</Link>
                    </div>
                    {showShareWarning && (
                        <div style={{
                            background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                            border: '2px solid #fbbf24',
                            borderRadius: '8px',
                            padding: '12px 20px',
                            marginBottom: '20px',
                            position: 'relative',
                            color: 'white'
                        }}>
                            <button
                                onClick={() => setShowShareWarning(false)}
                                className="groupshare-close-button"
                                style={{
                                    position: 'absolute',
                                    top: '8px',
                                    right: '8px',
                                    background: 'transparent',
                                    border: 'none',
                                    color: 'white',
                                    fontSize: '20px',
                                    cursor: 'pointer',
                                    padding: '0',
                                    width: '24px',
                                    height: '24px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderRadius: '4px',
                                    transition: 'background 0.2s'
                                }}
                                title="Dismiss warning"
                            >
                                ✕
                            </button>
                            <div style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'space-between',
                                paddingRight: '30px'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    <span style={{ fontSize: '1.5em' }}>⚠️</span>
                                    <div>
                                        <div style={{ fontWeight: 'bold', marginBottom: '2px' }}>
                                            Free Account: 1 Share Link Per Day
                                        </div>
                                        <div style={{ fontSize: '0.9em', opacity: 0.95 }}>
                                            You can generate only 1 shareable link every 24 hours. Save it for when you need it!
                                        </div>
                                    </div>
                                </div>
                                <Link
                                    to="/purchase"
                                    style={{
                                        background: 'white',
                                        color: '#d97706',
                                        padding: '6px 16px',
                                        borderRadius: '6px',
                                        textDecoration: 'none',
                                        fontWeight: 'bold',
                                        whiteSpace: 'nowrap',
                                        marginLeft: '20px'
                                    }}
                                >
                                    Upgrade for Unlimited
                                </Link>
                            </div>
                        </div>
                    )}
                </>
            )}
            {/* Stage 1: Acquisition */}
            <h2 style={{ color: '#7dd3fc', margin: '0 0 8px 0' }}>Stage 1: Acquisition</h2>
            {showDraftBanner && (
              <div className="groupshare-expiry-banner" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span>Found an unsaved draft. Restore it?</span>
                <span style={{ display: 'flex', gap: 8 }}>
                  <button className="button-secondary" onClick={discardDraft}>Discard</button>
                  <button className="button-primary" onClick={restoreDraft}>Restore</button>
                </span>
              </div>
            )}
            <div className="groupshare-section">
              {/* Acquisition Matrix */}
              <div className="calculator-panel" style={{ marginBottom: 12 }}>
                <h3>Acquisition Matrix</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1.5fr 1.5fr auto', gap: 8, marginBottom: 6, color: '#a0a4c2', fontSize: 12 }}>
                  <div>Cargo</div>
                  <div>Qty</div>
                  <div>SCU</div>
                  <div>Avg Price/SCU</div>
                  <div>Est. Total</div>
                  <div>Actions</div>
                </div>
                {(haulItems || []).map((item, index) => {
                  const qty = Number(item.quantity || 0);
                  const scu = Number(item.scu || 0);
                  // Use average acquisition price (merchant SELL price) for Stage 1 by default,
                  // falling back to market BUY average if SELL data is unavailable.
                  const avgPerScu = Number(
                    marketAvgSellByCommodity[item.commodityId]
                    || avgSellByCommodityFallback[item.commodityId] ||
                    marketAvgByCommodity[item.commodityId] || avgByCommodityFallback[item.commodityId] || 0
                  );
                  const effectivePerScu = Number((item.avgPrice || avgPerScu) || 0);
                  const estTotal = effectivePerScu * qty * scu;
                  return (
                    <div key={item.id || index} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1.5fr 1.5fr auto', gap: 8, alignItems: 'center', marginBottom: 6 }}>
                      <select name="commodityId" value={item.commodityId} onChange={e => handleHaulItemChange(index, e)} className="groupshare-select">
                        <option value="">Select Commodity</option>
                        {allCommodities.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                      <input name="quantity" type="number" value={item.quantity} onChange={e => handleHaulItemChange(index, e)} className="groupshare-input small-input" placeholder="Qty" />
                      <select name="scu" value={item.scu} onChange={e => handleHaulItemChange(index, e)} className="groupshare-select">
                        {(item.commodityId && item.locationId && getAcceptedScuSizes(item.commodityId, item.locationId).length > 0 ? getAcceptedScuSizes(item.commodityId, item.locationId) : scuOptions).map(s => <option key={s} value={s}>{s} scu</option>)}
                      </select>
                      <div className="groupshare-input read-only">{Number(effectivePerScu || 0).toFixed(2)}</div>
                      <div className="groupshare-input read-only">{Number(estTotal || 0).toFixed(2)}</div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button type="button" className="groupshare-button remove-button" onClick={() => removeHaulItem(index)}>-</button>
                      </div>
                    </div>
                  );
                })}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                  <button type="button" className="groupshare-button" onClick={addHaulItem}>Add Line</button>
                </div>
              </div>
              {/* End Acquisition Matrix */}

              {/* Additional Earnings under Acquisition */}
              <div className="calculator-panel" style={{ background: '#121826', border: '1px solid #26364d', borderRadius: 12, marginTop: 12 }}>
                <h3>Cash Earnings</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                      <div style={{ color: '#8fb9ff', fontWeight: 700 }}>Line Items</div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button type="button" className="button-secondary" onClick={() => setLineItems(prev => [...prev, { category: 'Trade', amount: '', notes: '' }])}>+ Item</button>
                        <button type="button" className="button-secondary" onClick={() => setLineItems(prev => prev.slice(0, -1))} disabled={lineItems.length === 0}>- Remove</button>
                      </div>
                    </div>
                    {(lineItems || []).map((li, idx) => (
                      <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 2fr auto', gap: 8, marginBottom: 6 }}>
                        <select
                          value={li.category}
                          onChange={e => setLineItems(prev => prev.map((x, i) => i === idx ? { ...x, category: e.target.value } : x))}
                          className="groupshare-select"
                        >
                          {[...walletCategories, 'GroupShare','Purchase','PurchaseShip','PurchaseComponent','PurchaseArmor','PurchaseWeapon','PurchaseOther'].map(k => <option key={k} value={k}>{k}</option>)}
                        </select>
                        <input
                          placeholder="Amount"
                          type="number"
                          value={li.amount}
                          onChange={e => setLineItems(prev => prev.map((x, i) => i === idx ? { ...x, amount: e.target.value } : x))}
                          className="groupshare-input"
                        />
                        <input
                          placeholder="Notes (optional)"
                          value={li.notes}
                          onChange={e => setLineItems(prev => prev.map((x, i) => i === idx ? { ...x, notes: e.target.value } : x))}
                          className="groupshare-input"
                        />
                        <button type="button" className="button-secondary" onClick={() => setLineItems(prev => prev.filter((_, i) => i !== idx))}>Remove</button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Stage 2: Route Planning */}
            <h2 style={{ color: '#7dd3fc', margin: '16px 0 8px 0' }}>Stage 2: Route Planning</h2>
            
            {/* Current Location Input - PRD-025: Updated with LocationCascadeSelector */}
            <div className="groupshare-section" style={{ background: 'linear-gradient(135deg, #1e3a5f 0%, #0f172a 100%)', border: '2px solid #3b82f6', padding: '20px', marginBottom: '20px' }}>
                <h3 style={{ color: '#60a5fa', marginBottom: '15px', fontSize: '1.2em' }}>📍 Your Current Location</h3>
                <LocationCascadeSelector
                    onChange={(location) => {
                        setCurrentLocation(location);
                        // Maintain backward compatibility with route suggestions
                        if (location) {
                            setSelectedSystem(location.system || '');
                            setSelectedMajorPlanet(location.planet || '');
                        } else {
                            setSelectedSystem('');
                            setSelectedMajorPlanet('');
                        }
                    }}
                    value={currentLocation}
                />
                {currentLocation && (
                    <div style={{ marginTop: '15px', padding: '10px', background: '#1e293b', borderRadius: '8px' }}>
                        <span style={{ color: '#10b981' }}>✓ Current Position: </span>
                        <strong style={{ color: '#60a5fa' }}>
                            {currentLocation.system}{currentLocation.planet ? ` / ${currentLocation.planet}` : ''} / {currentLocation.name}
                        </strong>
                    </div>
                )}
            </div>

            {/* Route Planning Mode Selection */}
            <div className="groupshare-section groupshare-settings-bar">
                <div className="settings-bar-item" style={{ flex: '1 1 100%' }}>
                    <div className="route-hint" style={{ marginBottom: '15px', fontSize: '1.1em' }}>
                        {(!selectedSystem || !selectedMajorPlanet) ? 
                            <span style={{ color: '#f59e0b' }}>⚠️ Please set your current location above to enable route planning.</span> : 
                            <span style={{ color: '#10b981' }}>✓ Location set. Choose your selling strategy:</span>
                        }
                    </div>
                    <div style={{ display: 'flex', gap: '15px', alignItems: 'stretch', flexWrap: 'wrap' }}>
                        <div style={{ flex: '1 1 200px', textAlign: 'center' }}>
                            <button
                                className="groupshare-button"
                                onClick={() => {
                                    if (!haulItems.some(item => item.commodityId && !item.actualPrice)) {
                                        setRouteToast('⚠️ No items to sell. Add items to your acquired cargo first.');
                                        return;
                                    }
                                    setRouteMode('fastSell');
                                    setShowRouteModal(true);
                                }}
                                style={{ 
                                    width: '100%',
                                    padding: '15px',
                                    fontSize: '1.1em',
                                    background: '#3b82f6',
                                    border: '2px solid #60a5fa'
                                }}
                                disabled={!selectedSystem || !selectedMajorPlanet}
                            >
                                ⚡ Fast Sell
                            </button>
                            <p style={{ color: '#94a3b8', fontSize: '0.9em', marginTop: '5px' }}>
                                Sell at the nearest locations for quick profits
                            </p>
                        </div>
                        <div style={{ flex: '1 1 200px', textAlign: 'center' }}>
                            <button
                                className="groupshare-button"
                                onClick={() => {
                                    if (!haulItems.some(item => item.commodityId && !item.actualPrice)) {
                                        setRouteToast('⚠️ No items to sell. Add items to your acquired cargo first.');
                                        return;
                                    }
                                    setRouteMode('bestPrice');
                                    setShowRouteModal(true);
                                }}
                                style={{ 
                                    width: '100%',
                                    padding: '15px',
                                    fontSize: '1.1em',
                                    background: '#10b981',
                                    border: '2px solid #34d399'
                                }}
                                disabled={!selectedSystem || !selectedMajorPlanet}
                            >
                                💎 Best Price
                            </button>
                            <p style={{ color: '#94a3b8', fontSize: '0.9em', marginTop: '5px' }}>
                                Travel further for the highest selling prices
                            </p>
                        </div>
                        <div style={{ flex: '1 1 200px', textAlign: 'center' }}>
                            <button
                                className="groupshare-button"
                                onClick={() => {
                                    setRouteMode('manual');
                                    setShowRouteModal(false);
                                    setRouteToast('📝 Manual mode: Set your own routes and prices.');
                                }}
                                style={{ 
                                    width: '100%',
                                    padding: '15px',
                                    fontSize: '1.1em',
                                    background: '#8b5cf6',
                                    border: '2px solid #a78bfa'
                                }}
                            >
                                ✏️ Manual
                            </button>
                            <p style={{ color: '#94a3b8', fontSize: '0.9em', marginTop: '5px' }}>
                                Manually select locations and set prices
                            </p>
                        </div>
                    </div>
                </div>
                <div className="settings-bar-item">
                    <button
                      className="groupshare-button"
                      onClick={() => {
                        setSelectedSystem('');
                        setSelectedMajorPlanet('');
                        setCurrentLocation(null); // PRD-025: Reset LocationCascadeSelector
                        startTransition(() => setRouteMode('fastSell'));
                        setRouteToast('Location and filters have been reset.');
                        setShowRouteModal(false);
                      }}
                      style={{ padding: '10px 20px' }}
                    >
                      🔄 Reset Location
                    </button>
                </div>
            </div>

            {/* Route Modal - Shows comparison between Fast and Best routes */}
            {showRouteModal && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0, 0, 0, 0.8)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000
                }}>
                    <div style={{
                        background: '#0f172a',
                        borderRadius: '12px',
                        padding: '30px',
                        maxWidth: '90%',
                        maxHeight: '80vh',
                        overflow: 'auto',
                        border: '2px solid #334155'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                            <h2 style={{ color: '#60a5fa', margin: 0 }}>
                                {routeMode === 'fastSell' ? '⚡ Fast Sell Route Preview' : '💎 Best Price Route Preview'}
                            </h2>
                            <button
                                onClick={() => setShowRouteModal(false)}
                                style={{
                                    background: 'transparent',
                                    border: 'none',
                                    color: '#94a3b8',
                                    fontSize: '24px',
                                    cursor: 'pointer'
                                }}
                            >
                                ×
                            </button>
                        </div>

                        {/* SCU Compatibility Check */}
                        <ScuIncompatibilityWarning
                            cargoItems={haulItems.filter(item => item.commodityId && Number(item.quantity) > 0 && !item.actualPrice).map(item => ({
                                ...item,
                                commodityName: allCommodities.find(c => c.id === item.commodityId)?.name || 'Unknown'
                            }))}
                            merchantCommodities={merchantCommodities}
                        />

                        {/* SCU Filtering Summary */}
                        {routeSuggestions && routeSuggestions.length > 0 && (
                            <div style={{
                                marginBottom: '15px',
                                padding: '12px',
                                background: 'linear-gradient(135deg, #0f172a 0%, rgba(16, 185, 129, 0.05) 100%)',
                                borderRadius: '8px',
                                border: '1px solid #10b981'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                                    <span style={{ color: '#10b981', fontWeight: 'bold' }}>✓ SCU Filter Active</span>
                                    <span style={{ color: '#94a3b8', fontSize: '0.9em' }}>
                                        Only showing locations that accept your cargo box sizes
                                    </span>
                                </div>
                                <div style={{ marginTop: '8px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                                    {[...new Set(haulItems.filter(i => i.commodityId && !i.actualPrice).map(i => Number(i.scu)))].sort((a,b) => a-b).map(size => (
                                        <span key={size} style={{
                                            padding: '4px 10px',
                                            background: 'rgba(16, 185, 129, 0.2)',
                                            border: '1px solid #10b981',
                                            borderRadius: '4px',
                                            fontSize: '0.8em',
                                            color: '#34d399'
                                        }}>
                                            {size} SCU
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Route Matrix */}
                        {orderedRouteSteps && orderedRouteSteps.length > 0 ? (
                            <div>
                                <div style={{ marginBottom: '20px' }}>
                                    <div style={{ display: 'flex', gap: '20px', marginBottom: '15px' }}>
                                        <div style={{ padding: '10px', background: '#1e293b', borderRadius: '8px', flex: 1 }}>
                                            <div style={{ color: '#94a3b8', fontSize: '0.9em' }}>Total Stops</div>
                                            <div style={{ color: '#60a5fa', fontSize: '1.5em', fontWeight: 'bold' }}>
                                                {orderedRouteSteps.length}
                                            </div>
                                        </div>
                                        <div style={{ padding: '10px', background: '#1e293b', borderRadius: '8px', flex: 1 }}>
                                            <div style={{ color: '#94a3b8', fontSize: '0.9em' }}>Est. Revenue</div>
                                            <div style={{ color: '#10b981', fontSize: '1.5em', fontWeight: 'bold' }}>
                                                {orderedRouteSteps.reduce((a,s)=>a+Number(s.estTotal||0),0).toFixed(0)} aUEC
                                            </div>
                                        </div>
                                        <div style={{ padding: '10px', background: '#1e293b', borderRadius: '8px', flex: 1 }}>
                                            <div style={{ color: '#94a3b8', fontSize: '0.9em' }}>Est. Profit</div>
                                            <div style={{ color: '#f59e0b', fontSize: '1.5em', fontWeight: 'bold' }}>
                                                {orderedRouteSteps.reduce((a,s)=>a+Number(s.estProfit||0),0).toFixed(0)} aUEC
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <table style={{ width: '100%', borderCollapse: 'collapse', background: '#1e293b', borderRadius: '8px', overflow: 'hidden' }}>
                                    <thead>
                                        <tr style={{ background: '#334155' }}>
                                            <th style={{ padding: '12px', textAlign: 'left', color: '#94a3b8' }}>Step</th>
                                            <th style={{ padding: '12px', textAlign: 'left', color: '#94a3b8' }}>Location</th>
                                            <th style={{ padding: '12px', textAlign: 'left', color: '#94a3b8' }}>Items</th>
                                            <th style={{ padding: '12px', textAlign: 'right', color: '#94a3b8' }}>Revenue</th>
                                            <th style={{ padding: '12px', textAlign: 'right', color: '#94a3b8' }}>Profit</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {orderedRouteSteps.map((step, i) => (
                                            <tr key={i} style={{ borderBottom: '1px solid #334155' }}>
                                                <td style={{ padding: '12px', fontWeight: 'bold' }}>{i + 1}</td>
                                                <td style={{ padding: '12px' }}>
                                                    <div style={{ fontWeight: 'bold', color: '#60a5fa' }}>{step.merchantName}</div>
                                                    <div style={{ fontSize: '0.9em', color: '#64748b' }}>{step.location}</div>
                                                </td>
                                                <td style={{ padding: '12px' }}>
                                                    {step.items.map((item, idx) => (
                                                        <div key={idx} style={{ marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                                            <span style={{ color: '#f59e0b' }}>{item.itemName}</span>
                                                            <span style={{
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                gap: '4px',
                                                                padding: '2px 8px',
                                                                background: 'rgba(16, 185, 129, 0.15)',
                                                                border: '1px solid #10b981',
                                                                borderRadius: '4px',
                                                                fontSize: '0.8em',
                                                                color: '#34d399'
                                                            }}>
                                                                <span style={{ color: '#10b981' }}>✓</span>
                                                                {item.qty} × {item.scu} SCU
                                                            </span>
                                                            <span style={{ color: '#64748b', fontSize: '0.8em' }}>
                                                                = {item.qty * item.scu} SCU total
                                                            </span>
                                                        </div>
                                                    ))}
                                                </td>
                                                <td style={{ padding: '12px', textAlign: 'right', color: '#10b981', fontWeight: 'bold' }}>
                                                    {Number(step.estTotal||0).toFixed(0)} aUEC
                                                </td>
                                                <td style={{ padding: '12px', textAlign: 'right', color: '#f59e0b', fontWeight: 'bold' }}>
                                                    +{Number(step.estProfit||0).toFixed(0)} aUEC
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>

                                <div style={{ marginTop: '20px', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                                    <button
                                        className="groupshare-button"
                                        onClick={() => setShowRouteModal(false)}
                                        style={{ padding: '10px 20px' }}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        className="groupshare-button"
                                        onClick={() => {
                                            // Apply all route suggestions
                                            routeSuggestions.forEach(s => applyRouteSuggestion(s.index, s.merchantId, s.price));
                                            setShowRouteModal(false);
                                            setRouteToast(`✅ ${routeMode === 'fastSell' ? 'Fast Sell' : 'Best Price'} route applied!`);
                                        }}
                                        style={{
                                            padding: '10px 20px',
                                            background: '#10b981',
                                            color: 'white',
                                            fontWeight: 'bold'
                                        }}
                                    >
                                        Apply This Route
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div style={{ padding: '40px', textAlign: 'center' }}>
                                <div style={{ fontSize: '3em', marginBottom: '15px', opacity: 0.5 }}>📦</div>
                                <div style={{ color: '#94a3b8', marginBottom: '10px', fontWeight: 'bold' }}>
                                    No Route Suggestions Available
                                </div>
                                <div style={{ color: '#64748b', fontSize: '0.9em', maxWidth: '400px', margin: '0 auto' }}>
                                    {haulItems.filter(i => i.commodityId && !i.actualPrice).length === 0
                                        ? 'Add items to your cargo first.'
                                        : 'No merchants found in this area that accept your cargo box sizes. Check the warning above for details on which sizes merchants accept.'}
                                </div>
                                {haulItems.filter(i => i.commodityId && !i.actualPrice).length > 0 && (
                                    <div style={{ marginTop: '15px' }}>
                                        <div style={{ color: '#94a3b8', fontSize: '0.8em', marginBottom: '8px' }}>Your current box sizes:</div>
                                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap' }}>
                                            {[...new Set(haulItems.filter(i => i.commodityId && !i.actualPrice).map(i => Number(i.scu)))].sort((a,b) => a-b).map(size => (
                                                <span key={size} style={{
                                                    padding: '4px 12px',
                                                    background: 'rgba(239, 68, 68, 0.15)',
                                                    border: '1px solid #ef4444',
                                                    borderRadius: '4px',
                                                    fontSize: '0.9em',
                                                    color: '#f87171'
                                                }}>
                                                    {size} SCU
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Manual Route Editor - Shows when manual mode is selected */}
            {routeMode === 'manual' && selectedSystem && selectedMajorPlanet && (
                <div className="route-suggestion-panel" style={{ marginBottom: '2rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                        <h3 style={{ color: '#a78bfa', margin: 0 }}>
                            ✏️ Manual Route Editor
                        </h3>
                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                            <span style={{ color: '#94a3b8', fontSize: '0.9em' }}>
                                Edit locations and prices for each cargo item
                            </span>
                        </div>
                    </div>

                    {/* Manual Route Table */}
                    <div style={{ background: '#0f172a', borderRadius: '8px', overflow: 'hidden' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ background: '#1e293b' }}>
                                    <th style={{ padding: '12px', textAlign: 'left', color: '#94a3b8' }}>Commodity</th>
                                    <th style={{ padding: '12px', textAlign: 'left', color: '#94a3b8' }}>Selling Location</th>
                                    <th style={{ padding: '12px', textAlign: 'center', color: '#94a3b8' }}>Qty</th>
                                    <th style={{ padding: '12px', textAlign: 'center', color: '#94a3b8' }}>SCU</th>
                                    <th style={{ padding: '12px', textAlign: 'right', color: '#94a3b8' }}>Price/SCU</th>
                                    <th style={{ padding: '12px', textAlign: 'right', color: '#94a3b8' }}>Total</th>
                                    <th style={{ padding: '12px', textAlign: 'center', color: '#94a3b8' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {haulItems
                                    .filter(item => !item.actualPrice)
                                    .map((item, index) => {
                                        const originalIndex = haulItems.indexOf(item);
                                        const commodity = allCommodities.find(c => c.id === item.commodityId);

                                        // Get ALL merchants that buy this commodity (not filtered by location)
                                        const availableMerchants = item.commodityId ? 
                                            (() => {
                                                const cid = String(item.commodityId);
                                                const buyingMerchantCommodities = merchantCommodities.filter(
                                                    mc => String(mc.commodity_id) === cid && String(mc.activity).toLowerCase() === 'buy'
                                                );
                                                
                                                // Debug logging
                                                if (buyingMerchantCommodities.length === 0) {
                                                    console.log(`No merchants found buying commodity ${cid}. Total merchant commodities: ${merchantCommodities.length}`);
                                                }
                                                
                                                const relevantMerchantIds = new Set(
                                                    buyingMerchantCommodities.map(mc => String(mc.merchant_id))
                                                );
                                                
                                                const merchants = allMerchants.filter(merchant => relevantMerchantIds.has(String(merchant.id)));
                                                console.log(`Found ${merchants.length} merchants for commodity ${commodity?.name || cid}`);
                                                return merchants;
                                            })() : [];
                                        
                                        return (
                                            <tr key={originalIndex} style={{ borderBottom: '1px solid #334155' }}>
                                                <td style={{ padding: '12px' }}>
                                                    <div style={{ 
                                                        fontWeight: 'bold', 
                                                        color: '#60a5fa',
                                                        padding: '6px'
                                                    }}>
                                                        {commodity?.name || `Item ${originalIndex + 1}`}
                                                    </div>
                                                    {!commodity && (
                                                        <div style={{ 
                                                            fontSize: '0.85em', 
                                                            color: '#ef4444',
                                                            padding: '0 6px'
                                                        }}>
                                                            ⚠️ Select commodity in Acquisition
                                                        </div>
                                                    )}
                                                </td>
                                                <td style={{ padding: '12px' }}>
                                                    <select
                                                        value={item.locationId || ''}
                                                        onChange={(e) => {
                                                            const merchantId = e.target.value;
                                                            if (!merchantId) {
                                                                const updatedItems = [...haulItems];
                                                                updatedItems[originalIndex] = {
                                                                    ...item,
                                                                    locationId: '',
                                                                    avgPrice: 0,
                                                                    totalUEC: 0
                                                                };
                                                                setHaulItems(updatedItems);
                                                                return;
                                                            }
                                                            
                                                            // Get price from charts data
                                                            const mc = merchantCommodities.find(
                                                                x => x.merchant_id === merchantId && 
                                                                x.commodity_id === item.commodityId && 
                                                                x.activity === 'buy'
                                                            );
                                                            let price = 0;
                                                            if (mc) {
                                                                price = mc.last_buy_price || 
                                                                       (mc.price_history && mc.price_history.length > 0 ? 
                                                                        mc.price_history[mc.price_history.length - 1].price : 0);
                                                            }
                                                            
                                                            const updatedItems = [...haulItems];
                                                            updatedItems[originalIndex] = {
                                                                ...item,
                                                                locationId: merchantId,
                                                                avgPrice: price,
                                                                totalUEC: price * item.quantity * item.scu
                                                            };
                                                            setHaulItems(updatedItems);
                                                        }}
                                                        style={{
                                                            width: '100%',
                                                            padding: '6px',
                                                            background: '#1e293b',
                                                            color: '#e2e8f0',
                                                            border: '1px solid #334155',
                                                            borderRadius: '4px'
                                                        }}
                                                        disabled={!item.commodityId}
                                                    >
                                                        <option value="">
                                                            {!item.commodityId ? 'Select commodity first' : 'Select Location'}
                                                        </option>
                                                        {availableMerchants.length > 0 ? (
                                                            availableMerchants.map(m => {
                                                                const mc = merchantCommodities.find(
                                                                    x => x.merchant_id === m.id && 
                                                                    x.commodity_id === item.commodityId && 
                                                                    x.activity === 'buy'
                                                                );
                                                                const price = mc ? (mc.last_buy_price || 
                                                                    (mc.price_history && mc.price_history.length > 0 ? 
                                                                     mc.price_history[mc.price_history.length - 1].price : 0)) : 0;
                                                                
                                                                // Get accepted SCU sizes for this merchant/commodity combo
                                                                const acceptedScus = mc?.accepted_scus || [];
                                                                const scuInfo = acceptedScus.length > 0 ? 
                                                                    ` [${acceptedScus.join(',')} SCU]` : '';
                                                                
                                                                return (
                                                                    <option key={m.id} value={m.id}>
                                                                        {m.name} - {m.system}/{m.majorPlanet || m.major_planet || m.planetary_body || m.planetaryBody} 
                                                                        {price > 0 ? ` (${price.toFixed(2)} aUEC/SCU)` : ' (No price data)'}
                                                                        {scuInfo}
                                                                    </option>
                                                                );
                                                            })
                                                        ) : (
                                                            <option value="" disabled>No merchants buy this commodity</option>
                                                        )}
                                                    </select>
                                                </td>
                                                <td style={{ padding: '12px', textAlign: 'center' }}>
                                                    {item.quantity}
                                                </td>
                                                <td style={{ padding: '12px', textAlign: 'center' }}>
                                                    {item.scu} SCU
                                                </td>
                                                <td style={{ padding: '12px', textAlign: 'right' }}>
                                                    {item.avgPrice ? `${item.avgPrice.toFixed(2)} aUEC` : '—'}
                                                </td>
                                                <td style={{ padding: '12px', textAlign: 'right', fontWeight: 'bold', color: '#10b981' }}>
                                                    {item.totalUEC ? `${item.totalUEC.toFixed(0)} aUEC` : '—'}
                                                </td>
                                                <td style={{ padding: '12px', textAlign: 'center' }}>
                                                    <button
                                                        onClick={() => {
                                                            setSellIndex(originalIndex);
                                                            setSellModalOpen(true);
                                                        }}
                                                        className="groupshare-button"
                                                        style={{
                                                            padding: '6px 12px',
                                                            background: '#10b981',
                                                            color: 'white'
                                                        }}
                                                        disabled={!item.locationId}
                                                    >
                                                        Sell
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                            </tbody>
                        </table>
                        {haulItems.filter(item => !item.actualPrice).length === 0 && (
                            <div style={{ padding: '20px', textAlign: 'center', color: '#64748b' }}>
                                No unsold items. Add items to your cargo or complete your sales.
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Simplified Route Display - Only show when route is applied and NOT in manual mode */}
            {routeSuggestions && routeSuggestions.length > 0 && !showRouteModal && routeMode !== 'manual' && (
                <div className="route-suggestion-panel" style={{ marginBottom: '2rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', flexWrap: 'wrap', gap: '10px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                            <h3 style={{ color: '#60a5fa', margin: 0 }}>
                                {routeMode === 'fastSell' ? '⚡ Active Route: Fast Sell' :
                                 '💎 Active Route: Best Price'}
                            </h3>
                            {/* SCU Filter Badge */}
                            <span style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                padding: '4px 10px',
                                background: 'rgba(16, 185, 129, 0.15)',
                                border: '1px solid #10b981',
                                borderRadius: '4px',
                                fontSize: '0.8em',
                                color: '#34d399'
                            }}>
                                <span style={{ color: '#10b981' }}>✓</span>
                                SCU Filtered: {[...new Set(haulItems.filter(i => i.commodityId && !i.actualPrice).map(i => Number(i.scu)))].sort((a,b) => a-b).join(', ')} SCU
                            </span>
                        </div>
                        <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
                            <span style={{ color: '#94a3b8' }}>
                                {orderedRouteSteps.length} stops •
                                <strong style={{ color: '#10b981', marginLeft: '5px' }}>
                                    {orderedRouteSteps.reduce((a,s)=>a+Number(s.estTotal||0),0).toFixed(0)} aUEC total
                                </strong>
                            </span>
                            <button
                                className="groupshare-button"
                                onClick={() => setShowRouteModal(true)}
                                style={{ padding: '8px 16px' }}
                            >
                                View Details
                            </button>
                            <button
                                className="groupshare-button"
                                onClick={() => {
                                    // Clear the route
                                    setHaulItems(prev => prev.map(item => ({
                                        ...item,
                                        locationId: '',
                                        avgPrice: 0,
                                        totalUEC: 0
                                    })));
                                    setRouteToast('Route cleared. Select a new route or set locations manually.');
                                }}
                                style={{ 
                                    padding: '8px 16px',
                                    background: '#ef4444',
                                    color: 'white'
                                }}
                            >
                                Clear Route
                            </button>
                        </div>
                    </div>
                    {routeToast && (
                        <div className="route-toast" style={{ marginBottom: '10px' }}>{routeToast}</div>
                    )}
                    
                    {/* Active Route Table */}
                    <div style={{ background: '#0f172a', borderRadius: '8px', overflow: 'hidden' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ background: '#1e293b' }}>
                                    <th style={{ padding: '12px', textAlign: 'left', color: '#94a3b8' }}>Item</th>
                                    <th style={{ padding: '12px', textAlign: 'left', color: '#94a3b8' }}>Location</th>
                                    <th style={{ padding: '12px', textAlign: 'center', color: '#94a3b8' }}>Quantity</th>
                                    <th style={{ padding: '12px', textAlign: 'center', color: '#94a3b8' }}>SCU Size</th>
                                    <th style={{ padding: '12px', textAlign: 'right', color: '#94a3b8' }}>Est. Profit</th>
                                    <th style={{ padding: '12px', textAlign: 'center', color: '#94a3b8' }}>Demand</th>
                                    <th style={{ padding: '12px', textAlign: 'center', color: '#94a3b8' }}>Status</th>
                                    <th style={{ padding: '12px', textAlign: 'center', color: '#94a3b8' }}>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {haulItems
                                    .filter(item => item.commodityId && !item.actualPrice)
                                    .map((item, index) => {
                                        const originalIndex = haulItems.indexOf(item);
                                        const commodity = allCommodities.find(c => c.id === item.commodityId);
                                        const merchant = allMerchants.find(m => m.id === item.locationId);
                                        const suggestion = routeSuggestions.find(s => s.index === originalIndex);
                                        
                                        // Calculate profitability
                                        const baseCost = Number(
                                            marketMinSellByCommodityScoped[item.commodityId] ||
                                            minSellByCommodityFallback[item.commodityId] ||
                                            marketAvgSellByCommodity[item.commodityId] ||
                                            avgSellByCommodityFallback[item.commodityId] ||
                                            0
                                        );
                                        const sellPrice = Number(item.avgPrice || suggestion?.price || 0);
                                        const profit = Math.max(0, (sellPrice - baseCost) * item.quantity * item.scu);
                                        
                                        // Determine demand level (simplified)
                                        const demand = sellPrice > baseCost * 1.5 ? 'High' : 
                                                      sellPrice > baseCost * 1.2 ? 'Medium' : 'Low';
                                        const demandColor = demand === 'High' ? '#10b981' : 
                                                           demand === 'Medium' ? '#f59e0b' : '#ef4444';
                                        
                                        return (
                                            <tr key={originalIndex} style={{ borderBottom: '1px solid #334155' }}>
                                                <td style={{ padding: '12px' }}>
                                                    <div style={{ fontWeight: 'bold', color: '#60a5fa' }}>
                                                        {commodity?.name || 'Unknown'}
                                                    </div>
                                                    {item.leftover && (
                                                        <span style={{
                                                            display: 'inline-block',
                                                            marginTop: '4px',
                                                            padding: '2px 6px',
                                                            background: '#f59e0b',
                                                            borderRadius: '4px',
                                                            fontSize: '0.8em',
                                                            color: 'white'
                                                        }}>
                                                            LEFTOVER
                                                        </span>
                                                    )}
                                                </td>
                                                <td style={{ padding: '12px' }}>
                                                    <div style={{ fontWeight: 'bold', color: '#94a3b8' }}>
                                                        {merchant?.name || 'Not Set'}
                                                    </div>
                                                    <div style={{ fontSize: '0.9em', color: '#64748b' }}>
                                                        {merchant?.system} / {getMerchantMajor(merchant)}
                                                    </div>
                                                </td>
                                                <td style={{ padding: '12px', textAlign: 'center', fontWeight: 'bold' }}>
                                                    {item.quantity}
                                                </td>
                                                <td style={{ padding: '12px', textAlign: 'center' }}>
                                                    {item.scu} SCU
                                                </td>
                                                <td style={{ padding: '12px', textAlign: 'right' }}>
                                                    <div style={{ fontWeight: 'bold', color: '#10b981' }}>
                                                        +{profit.toFixed(0)} aUEC
                                                    </div>
                                                    <div style={{ fontSize: '0.9em', color: '#64748b' }}>
                                                        @ {sellPrice.toFixed(0)}/SCU
                                                    </div>
                                                </td>
                                                <td style={{ padding: '12px', textAlign: 'center' }}>
                                                    <span style={{
                                                        padding: '4px 8px',
                                                        background: demandColor + '22',
                                                        color: demandColor,
                                                        borderRadius: '4px',
                                                        fontSize: '0.9em',
                                                        fontWeight: 'bold'
                                                    }}>
                                                        {demand}
                                                    </span>
                                                </td>
                                                <td style={{ padding: '12px', textAlign: 'center' }}>
                                                    <span style={{
                                                        padding: '4px 8px',
                                                        background: '#475569',
                                                        borderRadius: '4px',
                                                        fontSize: '0.9em'
                                                    }}>
                                                        Ready to Sell
                                                    </span>
                                                </td>
                                                <td style={{ padding: '12px', textAlign: 'center' }}>
                                                    <button
                                                        onClick={() => {
                                                            setSellIndex(originalIndex);
                                                            setSellModalOpen(true);
                                                        }}
                                                        className="groupshare-button"
                                                        style={{
                                                            padding: '8px 16px',
                                                            background: '#10b981',
                                                            color: 'white',
                                                            fontWeight: 'bold'
                                                        }}
                                                    >
                                                        💰 Sell
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                            </tbody>
                        </table>
                        
                        {haulItems.filter(item => item.commodityId && !item.actualPrice).length === 0 && (
                            <div style={{ padding: '20px', textAlign: 'center', color: '#64748b' }}>
                                All items have been sold or no items in route.
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Stage 3: Group Share */}
            <h2 style={{ color: '#7dd3fc', margin: '16px 0 8px 0' }}>Stage 3: Transactions</h2>

            <div className="groupshare-section" style={{ background: '#0f172a', borderRadius: '8px', padding: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    <h2 style={{ color: '#60a5fa', margin: 0 }}>📊 Transaction History</h2>
                    <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
                        <span style={{ color: '#94a3b8' }}>
                            Sold: <strong style={{ color: '#10b981' }}>
                                {haulItems.filter(item => Number(item.actualPrice || 0) > 0).length}
                            </strong>
                        </span>
                        <span style={{ color: '#94a3b8' }}>
                            Remaining: <strong style={{ color: '#f59e0b' }}>
                                {haulItems.filter(item => Number(item.actualPrice || 0) === 0).length}
                            </strong>
                        </span>
                        <span style={{ color: '#94a3b8' }}>
                            Total Revenue: <strong style={{ color: '#10b981' }}>
                                {haulItems
                                    .filter(item => Number(item.actualPrice || 0) > 0)
                                    .reduce((sum, item) => sum + Number(item.actualPrice || 0), 0)
                                    .toFixed(0)} aUEC
                            </strong>
                        </span>
                    </div>
                </div>
                
                {/* Sold Items Table */}
                {haulItems.filter(item => Number(item.actualPrice || 0) > 0).length > 0 && (
                    <div style={{ marginBottom: '30px' }}>
                        <h3 style={{ color: '#10b981', marginBottom: '15px' }}>✅ Completed Sales</h3>
                        <div style={{ background: '#1e293b', borderRadius: '8px', overflow: 'hidden' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr style={{ background: '#334155' }}>
                                        <th style={{ padding: '12px', textAlign: 'left', color: '#94a3b8' }}>Item</th>
                                        <th style={{ padding: '12px', textAlign: 'left', color: '#94a3b8' }}>Location</th>
                                        <th style={{ padding: '12px', textAlign: 'center', color: '#94a3b8' }}>Quantity</th>
                                        <th style={{ padding: '12px', textAlign: 'center', color: '#94a3b8' }}>SCU</th>
                                        <th style={{ padding: '12px', textAlign: 'right', color: '#94a3b8' }}>Est. Price</th>
                                        <th style={{ padding: '12px', textAlign: 'right', color: '#94a3b8' }}>Actual Sale</th>
                                        <th style={{ padding: '12px', textAlign: 'center', color: '#94a3b8' }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {haulItems
                                        .filter(item => Number(item.actualPrice || 0) > 0)
                                        .map((item, index) => {
                                            const originalIndex = haulItems.indexOf(item);
                                            const commodity = allCommodities.find(c => c.id === item.commodityId);
                                            const merchant = allMerchants.find(m => m.id === item.locationId);
                                            
                                            return (
                                                <tr key={originalIndex} style={{ borderBottom: '1px solid #1e293b' }}>
                                                    <td style={{ padding: '12px' }}>
                                                        <div style={{ fontWeight: 'bold', color: '#60a5fa' }}>
                                                            {commodity?.name || 'Unknown'}
                                                        </div>
                                                    </td>
                                                    <td style={{ padding: '12px', color: '#94a3b8' }}>
                                                        {merchant?.name || 'Unknown Location'}
                                                    </td>
                                                    <td style={{ padding: '12px', textAlign: 'center' }}>
                                                        {item.quantity}
                                                    </td>
                                                    <td style={{ padding: '12px', textAlign: 'center' }}>
                                                        {item.scu}
                                                    </td>
                                                    <td style={{ padding: '12px', textAlign: 'right', color: '#64748b' }}>
                                                        {Number(item.totalUEC || 0).toFixed(0)} aUEC
                                                    </td>
                                                    <td style={{ padding: '12px', textAlign: 'right' }}>
                                                        <strong style={{ color: '#10b981', fontSize: '1.1em' }}>
                                                            {Number(item.actualPrice || 0).toFixed(0)} aUEC
                                                        </strong>
                                                    </td>
                                                    <td style={{ padding: '12px', textAlign: 'center' }}>
                                                        <button 
                                                            onClick={() => {
                                                                const next = [...haulItems];
                                                                next[originalIndex] = { ...next[originalIndex], actualPrice: '' };
                                                                setHaulItems(next);
                                                            }} 
                                                            className="groupshare-button"
                                                            style={{ padding: '6px 12px', fontSize: '0.9em' }}
                                                        >
                                                            Undo
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
                
                {/* Remaining Items Table */}
                {haulItems.filter(item => Number(item.actualPrice || 0) === 0).length > 0 && (
                    <div>
                        <h3 style={{ color: '#f59e0b', marginBottom: '15px' }}>
                            📦 Remaining Cargo
                            {haulItems.some(item => item.leftover) && (
                                <span style={{ 
                                    marginLeft: '10px', 
                                    fontSize: '0.9em', 
                                    color: '#94a3b8' 
                                }}>
                                    (includes partial quantities from incomplete sales)
                                </span>
                            )}
                        </h3>
                        <div style={{ background: '#1e293b', borderRadius: '8px', overflow: 'hidden' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr style={{ background: '#334155' }}>
                                        <th style={{ padding: '12px', textAlign: 'left', color: '#94a3b8' }}>Item</th>
                                        <th style={{ padding: '12px', textAlign: 'left', color: '#94a3b8' }}>Location</th>
                                        <th style={{ padding: '12px', textAlign: 'center', color: '#94a3b8' }}>Quantity</th>
                                        <th style={{ padding: '12px', textAlign: 'center', color: '#94a3b8' }}>SCU</th>
                                        <th style={{ padding: '12px', textAlign: 'right', color: '#94a3b8' }}>Est. Value</th>
                                        <th style={{ padding: '12px', textAlign: 'center', color: '#94a3b8' }}>Status</th>
                                        <th style={{ padding: '12px', textAlign: 'center', color: '#94a3b8' }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {haulItems
                                        .filter(item => Number(item.actualPrice || 0) === 0)
                                        .map((item, index) => {
                                            const originalIndex = haulItems.indexOf(item);
                                            const commodity = allCommodities.find(c => c.id === item.commodityId);
                                            const merchant = allMerchants.find(m => m.id === item.locationId);
                                            
                                            return (
                                                <tr key={originalIndex} style={{ borderBottom: '1px solid #1e293b' }}>
                                                    <td style={{ padding: '12px' }}>
                                                        <div style={{ fontWeight: 'bold', color: '#60a5fa' }}>
                                                            {commodity?.name || 'Select Item'}
                                                        </div>
                                                        {item.leftover && (
                                                            <span style={{ 
                                                                display: 'inline-block',
                                                                marginTop: '4px',
                                                                padding: '2px 6px', 
                                                                background: '#f59e0b', 
                                                                borderRadius: '4px', 
                                                                fontSize: '0.8em',
                                                                color: 'white'
                                                            }}>
                                                                LEFTOVER
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td style={{ padding: '12px', color: '#94a3b8' }}>
                                                        {merchant?.name || 'Not Set'}
                                                    </td>
                                                    <td style={{ padding: '12px', textAlign: 'center' }}>
                                                        {item.quantity}
                                                    </td>
                                                    <td style={{ padding: '12px', textAlign: 'center' }}>
                                                        {item.scu}
                                                    </td>
                                                    <td style={{ padding: '12px', textAlign: 'right', color: '#94a3b8' }}>
                                                        {Number(item.totalUEC || 0).toFixed(0)} aUEC
                                                    </td>
                                                    <td style={{ padding: '12px', textAlign: 'center' }}>
                                                        <span style={{ 
                                                            padding: '4px 8px', 
                                                            background: '#475569', 
                                                            borderRadius: '4px',
                                                            fontSize: '0.9em'
                                                        }}>
                                                            Unsold
                                                        </span>
                                                    </td>
                                                    <td style={{ padding: '12px', textAlign: 'center' }}>
                                                        <button 
                                                            onClick={() => { 
                                                                setSellIndex(originalIndex); 
                                                                setSellModalOpen(true); 
                                                            }} 
                                                            className="groupshare-button"
                                                            style={{ 
                                                                padding: '6px 12px', 
                                                                fontSize: '0.9em',
                                                                background: '#10b981'
                                                            }}
                                                        >
                                                            Sell
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
                
                {haulItems.length === 0 && (
                    <div style={{ 
                        textAlign: 'center', 
                        padding: '40px', 
                        color: '#64748b',
                        background: '#1e293b',
                        borderRadius: '8px'
                    }}>
                        No transactions recorded yet. Add items to your acquired cargo and start selling!
                    </div>
                )}
            </div>

            {/* Legacy Transactions section - Hidden but kept for compatibility */}
            <div className="groupshare-section" style={{ display: 'none' }}>
                <h2>Transactions</h2>
                <div className="groupshare-haul-header">
                    <div className="groupshare-header-item">Item</div>
                    <div className="groupshare-header-item">Location</div>
                    <div className="groupshare-header-item">Quantity</div>
                    <div className="groupshare-header-item">SCU</div>
                    <div className="groupshare-header-item">Est. Price / SCU</div>
                    <div className="groupshare-header-item">Estimated Price</div>
                    <div className="groupshare-header-item">Actual Price</div>
                    <div className="groupshare-header-item">Actions</div>
                </div>
                {haulItems
                  .filter(item => Number(item.actualPrice || 0) > 0)
                  .map((item, index) => (
                <div key={index} className="groupshare-item">
                    <select name="commodityId" value={item.commodityId} onChange={(e) => handleHaulItemChange(index, e)} className="groupshare-select">
                        <option value="">Select Commodity</option>
                        {allCommodities.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                    <select name="locationId" value={item.locationId} onChange={(e) => handleHaulItemChange(index, e)} className="groupshare-select">
                        <option value="">Select Location</option>
                        {item.commodityId ? 
                            getMerchantsForCommodity(item.commodityId).map(m => 
                                <option key={m.id} value={m.id}>{m.name}</option>
                            ) : 
                            <option value="" disabled>Select a commodity first</option>
                        }
                    </select>
                    {/* Kiosk selector when selected location has kiosks */}
                    {item.locationId && Array.isArray((allMerchants.find(m => m.id === item.locationId) || {}).kiosks) && (allMerchants.find(m => m.id === item.locationId) || {}).kiosks.length > 0 && (
                      <select name="kioskId" value={item.kioskId || ''} onChange={(e) => handleHaulItemChange(index, e)} className="groupshare-select">
                        <option value="">Whole location (no kiosk)</option>
                        {(allMerchants.find(m => m.id === item.locationId).kiosks || []).map((k, i) => (
                          <option key={k.id || i} value={k.id || `${item.locationId}::${i}`}>{k.name} {Array.isArray(k.activities) && k.activities.length ? `(${k.activities.join('/')})` : ''}</option>
                        ))}
                      </select>
                    )}
                    <div className="groupshare-number">
                      <input 
                        type="number" 
                        id={`haul-quantity-${index}`}
                        name="quantity" 
                        value={item.quantity} 
                        onChange={(e) => handleHaulItemChange(index, e)} 
                        placeholder="Qty" 
                        className="groupshare-input small-input" 
                      />
                      <div className="stepper">
                        <button type="button" className="step-btn" onClick={() => handleHaulItemChange(index, { target: { name: 'quantity', value: Number(item.quantity || 0) + 1 } })}>▲</button>
                        <button type="button" className="step-btn" onClick={() => handleHaulItemChange(index, { target: { name: 'quantity', value: Math.max(0, Number(item.quantity || 0) - 1) } })}>▼</button>
                      </div>
                    </div>
                    <select name="scu" value={item.scu} onChange={(e) => handleHaulItemChange(index, e)} className="groupshare-select">
                        {item.commodityId && item.locationId ? 
                            getAcceptedScuSizes(item.commodityId, item.locationId).map(option => 
                                <option key={option} value={option}>{option} scu</option>
                            ) : 
                            <option value="" disabled>Select commodity and location first</option>
                        }
                    </select>
                    <input 
                  type="text" 
                  id={`haul-avg-price-${index}`}
                  name="avgPrice" 
                  value={item.avgPrice} 
                  readOnly 
                  placeholder="Est. Price/SCU" 
                  className="groupshare-input small-input" 
                />
                    <div className="groupshare-input read-only">{Number(item.totalUEC || 0).toFixed(2)}{item.leftover ? (<span className="leftover-badge">leftover</span>) : null}</div>
                    <input 
                  type="text" 
                  id={`haul-actual-price-${index}`}
                  name="actualPrice" 
                  value={item.actualPrice} 
                  onChange={(e) => handleHaulItemChange(index, e)} 
                  placeholder="Total Actual UEC" 
                  className="groupshare-input" 
                />
                    <div className="groupshare-item-actions">
                        {Number(item.actualPrice || 0) > 0 ? (
                          <>
                            <span className="sold-badge">Sold</span>
                            <button onClick={() => {
                              const next = [...haulItems];
                              next[index] = { ...next[index], actualPrice: '' };
                              setHaulItems(next);
                            }} className="groupshare-button">Clear Sale</button>
                          </>
                        ) : (
                          <button onClick={() => { setSellIndex(index); setSellModalOpen(true); }} className="groupshare-button">Sell</button>
                        )}
                        <button onClick={() => handleOpenMoveToInventoryModal(index)} className="groupshare-button move-btn">Move</button>
                        <button onClick={() => duplicateHaulItem(index)} className="groupshare-button">Duplicate</button>
                        <button onClick={() => removeHaulItem(index)} className="groupshare-button remove-button">-</button>
                    </div>
                </div>
                ))}
                <div className="groupshare-haul-total">
                    <span>Haul Total:</span>
                    <span>{haulTotalUEC.toFixed(2)} aUEC</span>
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center' }}>
                    <button onClick={() => {
                        const soldCount = haulItems.filter(h => Number(h.actualPrice || 0) > 0).length;
                        if (soldCount > 1) {
                            const ok = window.confirm(`Clear ${soldCount} recorded sale${soldCount!==1?'s':''}?`);
                            if (!ok) return;
                        }
                        setHaulItems(haulItems.map(h => ({ ...h, actualPrice: '' })));
                    }} className="button-secondary" style={{ flex: '0 0 auto' }}>Clear All Sells</button>
                </div>
            </div>

            {/* Summary Panel moved below Transactions */}
            <div className="calculator-panel" style={{ margin: '1rem 0' }}>
                <h3>Summary</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0,1fr))', gap: 8 }}>
                    <div>
                        <div className="note-muted">Haul Items</div>
                        <div>{haulItems.filter(h => h && h.commodityId && Number(h.quantity||0) > 0).length}</div>
                    </div>
                    <div>
                        <div className="note-muted">Cash Earnings</div>
                        <div>{Number(totalLineItems || 0).toFixed(2)} aUEC</div>
                    </div>
                    <div>
                        <div className="note-muted">Gross Total</div>
                        <div>{Number(totalUECEarned || 0).toFixed(2)} aUEC</div>
                    </div>
                    <div>
                        <div className="note-muted">Estimated Network Fee (0.25%)</div>
                        <div>{(Number(totalUECEarned || 0) * 0.0025).toFixed(2)} aUEC</div>
                    </div>
                    <div>
                        <div className="note-muted">Net Total (after fee)</div>
                        <div>{(Number(totalUECEarned || 0) * (1 - 0.0025)).toFixed(2)} aUEC</div>
                    </div>
                    <div>
                        <div className="note-muted">Notifiable Payees</div>
                        <div>{players.filter(p => p && p.notify && p.id).length}</div>
                    </div>
                </div>
            </div>



            <div className="calculator-panel">
                <h3>Player Share</h3>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 8 }}>
                    <button className="button-secondary" onClick={() => {
                        const n = players.length;
                        if (n <= 0) return;
                        const base = Math.floor((10000 / n)) / 100;
                        const assigned = base * n;
                        const remainder = Math.round((100 - assigned) * 100) / 100;
                        setPlayers(players.map((p, i) => ({ ...p, share: i === 0 ? base + remainder : base })));
                    }}>Split Evenly</button>
                    <button className="button-secondary" onClick={() => setPlayers(players.map(p => (p.paid ? p : { ...p, paid: true }))) }>Mark All Paid</button>
                </div>
                {Math.abs(totalPlayerSharePercentage - 100) > 0.01 && (
                     <div className="share-warning">
                        Total share percentage for all players must equal 100%. Current: {totalPlayerSharePercentage.toFixed(2)}%
                    </div>
                )}
                <div className="player-share-grid">
                    <div className="player-share-header">
                        <div>Player Name</div>
                        <div>Share %</div>
                        <div>Player Share (UEC)</div>
                        <div>Transaction Fee (UEC)</div>
                        <div>Net Player Share (UEC)</div>
                        <div>Paid</div>
                        <div>Notify</div>
                        <div>Remove</div>
                    </div>
                    {players.map((player, index) => {
                        const playerShareValue = totalUECEarned * (player.share / 100);
                        const transactionFee = playerShareValue * 0.0025;
                        const netPlayerShare = playerShareValue - transactionFee;

                        return (
                            <div key={index} className={`player-share-item ${player.paid ? 'paid-row' : ''}`}>
                                <div>
                                  <div className="username-autocomplete">
                                    <input
                                      type="text"
                                      id={`player-name-${index}`}
                                      name="sc_player_handle"
                                      value={player.name}
                                      onChange={e => handlePlayerChange(index, e)}
                                      placeholder="Player Name"
                                      className="groupshare-input"
                                      autoComplete="new-password"
                                      data-lpignore="true"
                                      data-1p-ignore="true"
                                      data-form-type="other"
                                    />
                                    {nameSuggestIdx === index && nameSuggestions.length > 0 && (
                                      <div className="autocomplete-menu">
                                        {nameSuggestions.map((s, i) => (
                                          <div key={s.id || i} className="autocomplete-item" onClick={() => selectSuggestedName(index, s)}>
                                            {s.username || 'Unknown User'}
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                </div>
                                <div>
                                  <input 
                                    type="number" 
                                    id={`player-share-${index}`}
                                    name="share" 
                                    value={player.share} 
                                    onChange={e => handlePlayerChange(index, e)} 
                                    className="groupshare-input"
                                    step="0.01"
                                    min="0"
                                    max="100"
                                  />
                                </div>
                                <div className="read-only-value">{playerShareValue.toFixed(2)}</div>
                                <div className="read-only-value">{transactionFee.toFixed(2)}</div>
                                <div className="net-share-cell">
                                    {netPlayerShare.toFixed(2)}
                                    <button onClick={() => handleCopy(netPlayerShare.toFixed(2), index)} className="button-copy">
                                        {copiedIndex === index ? 'Copied!' : 'Copy'}
                                    </button>
                                </div>
                                <div style={{ textAlign: 'center' }}>
                                    <input
                                        type="checkbox"
                                        checked={player.paid}
                                        onChange={(e) => handlePlayerPaidChange(index, e.target.checked)}
                                        className="paid-checkbox"
                                        title="Mark if this player has paid"
                                    />
                                </div>
                                <div style={{ textAlign: 'center' }}>
                                    {player.id ? (
                                        <input
                                          type="checkbox"
                                          checked={!!player.notify}
                                          onChange={(e) => {
                                            const np = [...players];
                                            np[index].notify = e.target.checked;
                                            setPlayers(np);
                                          }}
                                          title="Send notification to this player"
                                        />
                                    ) : (
                                      <span className="notify-disabled" title="Autocomplete a known user to enable notification">—</span>
                                    )}
                                </div>
                                <div style={{ textAlign: 'center' }}>
                                    <button
                                        onClick={() => removePlayer(index)}
                                        className="groupshare-button remove-button"
                                        style={{ padding: '4px 8px', minWidth: 'auto' }}
                                    >
                                        ✕
                                    </button>
                                </div>
                            </div>
                        )
                    })}
                    <div className="player-share-footer">
                        <div style={{ fontWeight: 'bold' }}>Total</div>
                        <div style={{ fontWeight: 'bold' }}>{totalPlayerSharePercentage.toFixed(2)}%</div>
                        <div style={{ fontWeight: 'bold' }}>{totalUECEarned.toFixed(2)}</div>
                        <div style={{ fontWeight: 'bold' }}>{(totalUECEarned * 0.0025).toFixed(2)}</div>
                        <div style={{ fontWeight: 'bold' }}>{(totalUECEarned * (1 - 0.0025)).toFixed(2)}</div>
                        <div></div>
                        <div></div>
                        <div></div>
                    </div>
                </div>
                 <button onClick={addPlayer} className="groupshare-button add-button">Add Player</button>
            </div>
            
            <div className="calculator-panel">
                <h3>Memo (Optional)</h3>
                <textarea value={memo} onChange={e => setMemo(e.target.value)} placeholder="Add any notes for this transaction..." />
            </div>

            <div className="actions-footer">
                <button onClick={resetAll} className="button-secondary">Reset All</button>
                <button onClick={handleCopyPayouts} className="button-secondary">Copy Payouts</button>
                <button onClick={handleExportCsv} className="button-secondary">Export CSV</button>
                
                {/* Show validation issues above the save button */}
                {isSaveDisabled && saveIssues.length > 0 && (
                    <div style={{
                        background: '#dc2626',
                        color: 'white',
                        padding: '8px 12px',
                        borderRadius: '6px',
                        fontSize: '0.9em',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        margin: '0 8px'
                    }}>
                        <span>⚠️</span>
                        <span>To save: {saveIssues.join(' & ')}</span>
                    </div>
                )}
                
                <button 
                    onClick={() => {
                        console.log('Save button clicked');
                        console.log('isSaveDisabled:', isSaveDisabled);
                        console.log('isSaving:', isSaving);
                        console.log('saveIssues:', saveIssues);
                        console.log('players:', players);
                        console.log('totalPlayerSharePercentage:', totalPlayerSharePercentage);
                        if (isSaveDisabled) {
                            alert(`Cannot save transaction:\n\n${saveIssues.join('\n')}\n\nCurrent share total: ${totalPlayerSharePercentage}%`);
                            return;
                        }
                        saveToDatabase();
                    }} 
                    className="button-primary" 
                    disabled={isSaveDisabled || isSaving} 
                    title={isSaveDisabled ? saveIssues.join(' · ') : ''}
                    style={{
                        opacity: isSaveDisabled ? 0.5 : 1,
                        cursor: isSaveDisabled ? 'not-allowed' : 'pointer'
                    }}
                >
                    {isSaving ? 'Saving…' : (transactionId ? 'Update Transaction' : 'Save Transaction')}
                </button>
                {players.length === 1 && (
                  <button className="button-secondary" onClick={() => {
                    setPlayers(players.map((p, i) => i === 0 ? {
                      ...p,
                      paid: true,
                      share: 100,
                      name: currentUser?.username || p.name,
                      id: currentUser?.id || p.id
                    } : p));
                  }}>Set Solo (100% & Paid)</button>
                )}
                <button 
                    onClick={() => {
                        // Check if user is authenticated first
                        if (!currentUser) {
                            generateShareLink(); // Will handle auth check and redirect
                            return;
                        }
                        
                        // Show warning for free users
                        if (!hasPremiumAccess && currentUser?.tier !== 'paid' && !currentUser?.org_entitled) {
                            const proceed = window.confirm(
                                "⚠️ FREE ACCOUNT - DAILY LIMIT WARNING\n\n" +
                                "You can only generate 1 share link per day as a free user.\n\n" +
                                "⏰ Once used:\n" +
                                "• You cannot generate another link until tomorrow\n" +
                                "• This link will expire in 24 hours\n" +
                                "• You'll need to wait until midnight (your local time) for reset\n\n" +
                                "💡 Tip: Save your daily share for when you really need it!\n\n" +
                                "🚀 Upgrade to Premium for:\n" +
                                "• Unlimited share links per day\n" +
                                "• Links still expire after 24 hours\n\n" +
                                "Are you sure you want to use your ONLY daily share now?\n\n" +
                                "[OK = Yes, use it now | Cancel = Save for later]"
                            );
                            if (!proceed) {
                                setRouteToast('💡 Share link saved for later. You can use it anytime today!');
                                setTimeout(() => setRouteToast(''), 5000);
                                return;
                            }
                        }
                        generateShareLink();
                    }} 
                    className="button-secondary"
                    style={{ 
                        background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                        color: 'white',
                        border: '1px solid #60a5fa',
                        position: 'relative'
                    }}
                    title={!hasPremiumAccess ? "Free users: 1 share per day" : "Generate shareable link"}
                >
                    🔗 Generate Share Link
                    {!hasPremiumAccess && currentUser && (
                        <span style={{
                            position: 'absolute',
                            top: '-8px',
                            right: '-8px',
                            background: '#f59e0b',
                            color: 'white',
                            borderRadius: '50%',
                            width: '20px',
                            height: '20px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '12px',
                            fontWeight: 'bold',
                            border: '2px solid #0f172a'
                        }}>
                            1
                        </span>
                    )}
                </button>
                {orgMemberships && orgMemberships.length > 0 && (
                  <button
                    onClick={openSaveToOrg}
                    className="button-primary"
                    style={{
                      opacity: hasPremiumAccess ? 1 : 0.6,
                      cursor: hasPremiumAccess ? 'pointer' : 'not-allowed',
                      position: 'relative'
                    }}
                    title={hasPremiumAccess ? 'Save to organization wallet' : 'Premium feature - upgrade to save to org wallet'}
                  >
                    Save to Org {!hasPremiumAccess && <span style={{
                      fontSize: '0.7em',
                      backgroundColor: '#fbbf24',
                      color: '#000',
                      padding: '2px 6px',
                      borderRadius: '4px',
                      marginLeft: '6px',
                      fontWeight: 'bold'
                    }}>PRO</span>}
                  </button>
                )}
            </div>
            <SelectLocationModal 
                isOpen={isLocationModalOpen}
                onClose={() => setLocationModalOpen(false)}
                locations={allMerchants}
                onConfirm={handleConfirmMoveToInventory}
            />
            {sellModalOpen && sellIndex !== null && (
              <TransactionModal
                open={sellModalOpen}
                onClose={() => { setSellModalOpen(false); setSellIndex(null); }}
                cargoItem={{ id: String(sellIndex), name: (allCommodities.find(c => c.id === haulItems[sellIndex].commodityId)?.name) || 'Item', qty: Number(haulItems[sellIndex].quantity) || 0, scu: Number(haulItems[sellIndex].scu) || 0 }}
                defaultRevenue={Number(haulItems[sellIndex].totalUEC || 0)}
                defaultQty={Number(haulItems[sellIndex].quantity) || 0}
                onConfirm={async ({ qtySold, totalRevenue }) => {
                  const idx = sellIndex;
                  if (idx === null || idx < 0 || idx >= haulItems.length) return;
                  const next = [...haulItems];
                  const original = { ...next[idx] };
                  const originalQty = Number(original.quantity) || 0;
                  const qty = Math.max(0, Math.min(Number(qtySold) || 0, originalQty));
                  const remaining = Math.max(0, originalQty - qty);
                  // Sold item (updates current index)
                  const soldItem = {
                    ...original,
                    quantity: qty,
                    actualPrice: Number(totalRevenue || 0).toFixed(2),
                    totalUEC: (Number(original.avgPrice) || 0) * qty * (Number(original.scu) || 0),
                  };
                  next[idx] = soldItem;
                  
                  // Save price data immediately when item is sold
                  if (original.locationId && original.commodityId && totalRevenue > 0) {
                    const pricePerSCU = Number(totalRevenue) / (qty * Number(original.scu));
                    const priceData = [{
                      commodity_id: original.commodityId,
                      merchant_id: original.locationId,
                      price: pricePerSCU,
                      activity: 'buy', // Merchant is buying from player
                      accepted_scus: [Number(original.scu)],
                      quantity: qty
                    }];

                    const result = await savePriceDataFromGroupShare(priceData);
                    if (result.success) {
                      console.log('Price data saved to charts');
                      // PRD-042 Phase 5: Track contribution for summary
                      setSubmittedPricesCount(prev => prev + 1);
                      const commodity = allCommodities.find(c => c.id === original.commodityId);
                      if (commodity?.name) {
                        setSubmittedCommodityNames(prev => [...new Set([...prev, commodity.name])]);
                      }
                      setRouteToast(`✅ Price data saved to community charts!`);
                      setTimeout(() => setRouteToast(''), 3000);
                    } else {
                      console.error('Failed to save price data:', result.error);
                    }
                  }
                  
                  // Remaining item (insert after) if any
                  if (remaining > 0) {
                    const remainingItem = {
                      ...original,
                      quantity: remaining,
                      actualPrice: '',
                      totalUEC: (Number(original.avgPrice) || 0) * remaining * (Number(original.scu) || 0),
                      leftover: true,
                    };
                    next.splice(idx + 1, 0, remainingItem);
                  }
                  setHaulItems(next);
                }}
              />
            )}
            {showShareModal && (
              <div style={{ 
                  background: '#0008', 
                  position: 'fixed', 
                  inset: 0, 
                  zIndex: 1000, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center' 
              }}>
                <div style={{ 
                    background: '#0b1220', 
                    border: '1px solid #1d3352', 
                    borderRadius: 12, 
                    padding: 24, 
                    minWidth: 500,
                    maxWidth: '90%'
                }}>
                  <div style={{ color: '#7dd3fc', fontWeight: 800, marginBottom: 16, fontSize: '1.2em' }}>
                    🔗 Share Link Generated
                    {isLiveShare && (
                        <span style={{ 
                            marginLeft: '10px',
                            padding: '4px 8px',
                            background: '#10b981',
                            borderRadius: '4px',
                            fontSize: '0.6em',
                            fontWeight: 'normal'
                        }}>
                            LIVE
                        </span>
                    )}
                  </div>
                  <div style={{ marginBottom: 16 }}>
                    <p style={{ color: '#94a3b8', marginBottom: 12 }}>
                      Share this link to give read-only access to your group share data:
                    </p>
                    <div style={{ 
                        display: 'flex', 
                        gap: 8,
                        alignItems: 'center',
                        background: '#1e293b',
                        padding: '12px',
                        borderRadius: 8,
                        border: '1px solid #334155'
                    }}>
                      <input 
                        type="text" 
                        value={shareLink} 
                        readOnly
                        style={{
                          flex: 1,
                          background: 'transparent',
                          border: 'none',
                          color: '#60a5fa',
                          fontSize: '0.9em',
                          outline: 'none'
                        }}
                        onClick={(e) => e.target.select()}
                      />
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(shareLink);
                          setRouteToast('✅ Link copied to clipboard!');
                          setTimeout(() => setRouteToast(''), 3000);
                        }}
                        style={{
                          padding: '6px 12px',
                          background: '#3b82f6',
                          color: 'white',
                          border: 'none',
                          borderRadius: 4,
                          cursor: 'pointer',
                          fontSize: '0.9em'
                        }}
                      >
                        📋 Copy
                      </button>
                    </div>
                  </div>
                  <div style={{ color: '#64748b', fontSize: '0.85em', marginBottom: 16 }}>
                    <p>📊 This link includes:</p>
                    <ul style={{ marginLeft: 20, marginTop: 8 }}>
                      <li>All acquired cargo items</li>
                      <li>Route planning and locations</li>
                      <li>Transaction history</li>
                      <li>Player shares and payouts</li>
                      <li>Profit calculations</li>
                    </ul>
                    {isLiveShare && (
                      <div style={{
                        marginTop: 12,
                        padding: '8px 12px',
                        background: '#1e293b',
                        borderRadius: 6,
                        border: '1px solid #334155'
                      }}>
                        <p style={{ color: '#f59e0b', margin: 0 }}>
                          ⏰ <strong>Expires in 24 hours</strong>
                        </p>
                        <p style={{ fontSize: '0.9em', margin: '4px 0 0 0' }}>
                          {hasPremiumAccess ?
                            'Premium users can create unlimited shares.' :
                            'Free users: 1 share per day. Upgrade for unlimited.'}
                        </p>
                      </div>
                    )}
                  </div>
                  {/* PRD-042 Phase 5: Price Contribution Summary */}
                  <PriceContributionSummary
                    pricesSubmitted={submittedPricesCount}
                    commodityNames={submittedCommodityNames}
                    onViewCharts={() => {
                      setShowShareModal(false);
                      navigate('/charts');
                    }}
                  />
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                    <button 
                      className="btn-neon-outline" 
                      onClick={() => setShowShareModal(false)}
                    >
                      Close
                    </button>
                    <button 
                      className="btn-neon"
                      onClick={() => {
                        window.open(shareLink, '_blank');
                      }}
                    >
                      🔗 Open Link
                    </button>
                  </div>
                </div>
              </div>
            )}
            {showSaveModal && (
              <div style={{ background: '#0008', position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ background: '#0b1220', border: '1px solid #1d3352', borderRadius: 12, padding: 16, minWidth: 380 }}>
                  <div style={{ color: '#7dd3fc', fontWeight: 800, marginBottom: 8 }}>Save to Org</div>
                  {!confirmSave ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {(!orgOptions || orgOptions.length === 0) && (
                        <div style={{ color: '#94a3b8' }}>You are not a member of any org.</div>
                      )}
                      {orgOptions && orgOptions.length > 0 && (
                        <>
                          <label style={{ color: '#9ca3af' }}>Organization</label>
                          <select className="select-neon" value={selectedOrg} onChange={e => handleSelectOrg(e.target.value)}>
                            <option value="">Select org…</option>
                            {orgOptions.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                          </select>
                          <label style={{ color: '#9ca3af' }}>Wallet</label>
                          <select className="select-neon" value={selectedWallet} onChange={e => setSelectedWallet(e.target.value)} disabled={!selectedOrg}>
                            <option value="">Select wallet…</option>
                            {walletOptions.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                          </select>
                        </>
                      )}
                      {orgSaveError && <div style={{ color: '#f87171' }}>{orgSaveError}</div>}
                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
                        <button className="btn-neon-outline" onClick={() => setShowSaveModal(false)}>Cancel</button>
                        <button className="btn-neon" onClick={continueToConfirm} disabled={!selectedOrg || !selectedWallet}>Continue</button>
                      </div>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <div style={{ color: '#e2e8f0' }}>Confirm saving <strong>{Number(totalUECEarned || 0).toFixed(2)} aUEC</strong> to selected org wallet?</div>
                      <label style={{ color: '#9ca3af', display: 'flex', alignItems: 'center', gap: 8 }}>
                        <input type="checkbox" checked={splitPerMember} onChange={e => setSplitPerMember(e.target.checked)} />
                        Split into per-member payouts (based on share %)
                      </label>
                      {orgSaveError && <div style={{ color: '#f87171' }}>{orgSaveError}</div>}
                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                        <button className="btn-neon-outline" onClick={() => setShowSaveModal(false)}>No</button>
                        <button className="btn-neon" onClick={handleConfirmSave} disabled={isSavingOrg}>{isSavingOrg ? 'Saving…' : 'Yes, Save'}</button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
        </div>
    );
};

export default GroupSharePage; 